import {
  Ref,
  computed,
  markRaw,
  reactive,
  readonly,
  ref,
  shallowRef,
  toRef,
  watch,
} from 'vue';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import {
  isNativePrintingStrategy,
  printingStrategyNative,
} from './printing/printing-strategy-native';
import { printingStrategyNativeLegacy } from './printing/printing-strategy-native-legacy';
import {
  hasUnsafeRetrySafety,
  jobStatusOfError,
  retrySafetyOf,
} from '@/lib/print/retry-safety';
import { usePrintHealthStore } from '@/stores/print-health.store';
import { delayedPrintMinutesOf } from '@/lib/print/print-health.utils';
import {
  JOBS_LOG_MAX,
  clearJobsLog,
  readJobsLog,
  writeJobsLog,
} from '@/lib/print/jobs-log';
import {
  attachFailedJobBytes,
  failedJobBytesOf,
} from '@/lib/print/failed-job-bytes';
import { v4 as uuidv4 } from 'uuid';
import { IPrinter } from '@/interfaces/IPrinter';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';
import PrintService, {
  PrintPluginStatus,
  PrinterType,
} from '@/modules/PrintService';
import { Globals } from '@/modules/Globals';
import {
  isLegacyPluginStrategy,
  printingStrategyPlugin,
} from './printing/printing-strategy-plugin';
import { printingStrategyBridge } from './printing/printing-strategy-bridge';
import { detectBridge } from './printing/use-plugin-detection';
import {
  clearSnooze,
  consumeUpgradeSeen,
  isUpgradeAvailable,
} from './printing/use-printing-upgrade';
import { event as gtagEvent } from 'vue-gtag';
import { IBridgeIdentity } from '@/interfaces/print-bridge-protocol';
import {
  isOrderAutoPrintable,
  resolveTicketAutoPrintMode,
  shouldAutoPrint,
  shouldDeferToPaymentGroup,
} from './printing/auto-print-policy';
import { createGroupPaymentAggregator } from './printing/group-payment-print';
import { resolveGroupPrintPlan } from './printing/group-print-plan';
import { deriveOpenTableSession } from '@/domain/pos/table-session-derivation';
import { useOrdersStore } from '@/stores/orders.store';
import { useLocalStorage } from '@vueuse/core';
import { IGroupPaymentInfo, IOrder } from '@/interfaces/IOrder';
import { OrderPaymentStatus, OrderStatus, OrderType } from '@/enums/OrderEnums';
import EscPosEncoder from '@/lib/print/EscPosEncoder';
import { renderQr } from '@/lib/print/qr-render';
import { getLogoBytes } from '@/lib/print/logo-cache';
import { buildArAfipEmitter } from '@/lib/invoicing/ar-afip-emitter';
import {
  displayPrice,
  getDisplayOptions,
  getOptionQuantityPrefix,
  roundPrice,
} from '@/utils';
import {
  getDayCloseCollectedDisplay,
  getDayCloseNetDisplay,
} from '@/domain/stats/day-close-display';
import { vueData } from '@/VueData';
import { AlertType } from '@/interfaces/IAlert';
import { LabelPrinter } from '@/lib/print/LabelPrinter';
import {
  computeStickerIndexContext,
  resolveStickerLineStartIndex,
} from '@/lib/print/sticker-indexing';
import OrdersService from '@/api/services/orders.service';
import { ICaja } from '@/interfaces/ICaja';
import { ICajaMovement } from '@/interfaces/ICajaMovement';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { ICajaResume, SalesByPaymentMethod } from '@/interfaces/ICajaResume';
import { IDayClose, DayClosePaymentBucket } from '@/interfaces/IDayClose';
import { IInvoicingItem } from '@/interfaces/IInvoicingItem';
import { buildAfipQrUrl } from '@/lib/invoicing/ar-afip-qr-payload';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { parseOrderPaymentsByType } from '@/utils/orders.utils';
import {
  computeMarkableComandaUnits,
  IComandaStationOutcome,
} from '@/utils/comanda-marking.utils';
import { IPrintJob } from '@/interfaces/printing.interfaces';
import {
  IEscposPrinterSettings,
  IPrinterSettingsEntry,
  IZplPrinterSettings,
  normalizeEscposSettings,
  normalizeZplSettings,
  stickerToZplSettings,
  zplSettingsToSticker,
} from '@/lib/print/printer-settings';
import { useMenuStore } from './use-menu';
import type { IReservationPrintOp } from '@/domain/reservations/reservations-print';
import { IBranch } from '@/interfaces/IBranch';
import { ICategory } from '@/interfaces/ICategory';
import { ICurrency } from '@/interfaces/ICurrency';
import { IRestaurant } from '@/interfaces/IRestaurant';
import { getTopLevelDomain } from '@/utils/domain.utils';
import { PAYMENT_METHODS_MAP } from '@/const/payment.consts';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { PaymentType } from '@/enums/PaymentEnums';
import { usePrintedStore } from '@/stores/printed.store';
import {
  computeManualPrintGateState,
  IManualPrintGate,
} from '@/utils/print-gate.utils';
import { usePrintingV2Store } from '@/stores/printing-v2.store';
import { ORDER_TYPES_MAP } from '@/const/orders.const';
import { computeClubTicketLines } from './printing/club-ticket-line';
import { printCajaResumeCanceledItems } from './printing/caja-resume-canceled-items';
import { computeOrderDiscountLines } from './printing/discount-lines';
import {
  ComandaBatchError,
  comandaDocDedupeKey,
  creditedComandaUnits,
  planComandaStationDocs,
} from './printing/comanda-unit-docs';
import { ComandaSplit, isComandaSplit } from '@/enums/ComandaSplit';

/**
 * Platforms that can ever print: desktop browsers (plugin/bridge) and
 * Capacitor ANDROID builds (native). Mobile/tablet browsers — and iOS builds,
 * which behave like a mobile browser for printing — can never print, so
 * print controls should be hidden there entirely.
 */
export const deviceCanEverPrint =
  Globals.isCapacitorAndroid || !Globals.isMobile;

const paymentTypesTitles = {
  cash: 'Efectivo',
  card: 'Tarjeta',
  online: 'Online',
  qr: 'QR',
  invitation: 'Invitación',
  employee: 'Empleado',
};

/**
 * Optional per-document context threaded from printOrder down to the
 * strategy: lets the bridge strategy set the job's dedupeKey (auto-print
 * cross-tab dedupe) and meta. Legacy/native strategies ignore it.
 */
export interface IPrintContext {
  dedupeKey?: string;
  kind?: 'ticket' | 'comanda' | 'sticker' | 'test';
  label?: string;
  /** comanda only — lets the jobs UI re-resolve the CURRENT assignment on reprint */
  stationId?: number;
}

/**
 * Carries partial progress out of a mid-batch sticker failure: unlike a
 * ticket (one document), a sticker batch is one job PER LABEL (see
 * ComandaBatchError for the comanda counterpart), so
 * the labels that DID print must be marked before the error propagates —
 * otherwise catch-up would duplicate them (safe failure) or the blanket
 * marking would swallow never-attempted labels (unsafe failure).
 */
export class StickerBatchError extends Error {
  readonly cause: unknown;
  /** Labels printed in THIS run, keyed by order-item index. */
  readonly printedByIndex: Map<number, number>;
  /** Index of the item whose label was in flight when the batch failed. */
  readonly failedIndex: number;

  constructor(
    cause: unknown,
    printedByIndex: Map<number, number>,
    failedIndex: number
  ) {
    super(
      cause instanceof Error ? cause.message : 'Error imprimiendo etiquetas'
    );
    this.name = 'StickerBatchError';
    this.cause = cause;
    this.printedByIndex = printedByIndex;
    this.failedIndex = failedIndex;
  }
}

/** One physical comanda document of a station batch (see _printComandaStationDocs). */
interface IComandaDocJob {
  items: IOrderItem[];
  /** Jobs-tab title and bridge meta.label of the document. */
  title: string;
  dedupeKey?: string;
  unitMarker?: string;
  /** Units the document accounts for, by order-item index. */
  units: ReadonlyMap<number, number>;
}

/**
 * Raw-bytes print request threaded from the store into the strategies.
 * Either `printer` or `id`+`type` identifies the target; `bytes` and
 * `base64` carry the same rendered document in both encodings (each
 * strategy consumes the one its transport needs).
 */
export interface IPrintRawBytesParams {
  printer?: IPrinter;
  id?: string | number;
  type?: 'usb' | 'bluetooth' | 'tcp';
  bytes: number[];
  base64: string;
  printContext?: IPrintContext;
}

export interface IPrintingStrategy {
  getPrinters: (mode: 'usb' | 'bluetooth') => Promise<IPrinter[]>;
  printRawBytes: (data: IPrintRawBytesParams) => Promise<void>;
  status: Ref<PrintPluginStatus>;
  connect: () => void;
  disconnect?: () => void;
  setStore: (store: ReturnType<typeof usePrintingStore>) => void;
}

export type BridgeMode = 'unknown' | 'legacy' | 'bridge' | 'none';

function uint8ArrayToBase64(uint8Array) {
  // Convert the Uint8Array to a regular array
  const regularArray = Array.from(uint8Array);

  // Convert each byte to its corresponding character
  const charArray = regularArray.map((byte) =>
    String.fromCharCode(byte as any)
  );

  // Create a string from the character array and encode it in base64.
  const base64String = btoa(charArray.join(''));

  return base64String;
}

export function getDefaultPrintingSettings() {
  return <IPrintingSettings>{
    trigger: TicketPrintSetting.Manual,
    source: PrintSource.ThisDevice,
    encoding: 'cp858',
  };
}

/**
 * Saved TCP network printer (native strategy only). Unlike usb/bluetooth,
 * network printers cannot be discovered on demand cheaply, so the full config
 * persists and reloadAllPrinters() appends them to the discovered list.
 */
export interface INetworkPrinterConfig {
  name: string;
  host: string;
  port: number;
  /**
   * Whether the printer answered the DLE EOT probe. Enables the post-send
   * status check on its native handle (see probeNetworkPrinter in the
   * plugin). Written ONLY through setNetworkPrinterDleEot.
   */
  supportsDleEot: boolean;
}

/**
 * Per-printer hardware settings persisted by the NATIVE strategy, keyed by
 * printer name (usb/bt/network alike — identity is name-based on native).
 * Values stay Partial: normalization to full shapes happens on read
 * (printerSettingsOf), so a rollback→re-upgrade or a hand-edited payload
 * can never crash a renderer.
 */
export interface IPersistedPrinterSettings {
  type: 'escpos' | 'zpl';
  escpos?: Partial<IEscposPrinterSettings>;
  zpl?: Partial<IZplPrinterSettings>;
}

export interface IPersistedPrintingSettings {
  stickerPrinterName: string | null;
  comandaPrinterNamesByStation: Record<number, string | null>;
  ticketPrinterName: string | null;
  stickerPrintingSettings: IPrintingSettings;
  comandaPrintingSettings: IPrintingSettings;
  ticketPrintingSettings: IPrintingSettings;
  networkPrinters: INetworkPrinterConfig[];
  /**
   * ROLLBACK NOTE: an old build's first persist() strips this map (explicit
   * field lists); the destination-encoding dual-write keeps printing correct
   * on the old build, and the migration below re-seeds the map on
   * re-upgrade.
   */
  printerSettings?: Record<string, IPersistedPrinterSettings>;
}

/** Shape persisted before print stations existed (2 hardcoded destinations). */
interface ILegacyPersistedPrintingSettings
  extends Omit<IPersistedPrintingSettings, 'comandaPrinterNamesByStation'> {
  comandaPrinterNamesByStation?: Record<number, string | null>;
  comandaCajaPrinterName?: string | null;
  comandaCocinaPrinterName?: string | null;
}

/** Encoder pre-configured with a printer's resolved hardware settings. */
function encoderForSettings(escpos: IEscposPrinterSettings): EscPosEncoder {
  return new EscPosEncoder({
    paperWidthMm: escpos.paperWidthMm,
    charsPerLineNormal: escpos.charsPerLineNormal,
    charsPerLineSmall: escpos.charsPerLineSmall,
    model: escpos.model,
  });
}

export const usePrintingStore = defineStore('printing', () => {
  const persistedSettings = useLocalStorage<IPersistedPrintingSettings>(
    'printing-settings',
    {
      stickerPrinterName: null,
      comandaPrinterNamesByStation: {},
      ticketPrinterName: null,
      stickerPrintingSettings: getDefaultPrintingSettings(),
      comandaPrintingSettings: getDefaultPrintingSettings(),
      ticketPrintingSettings: getDefaultPrintingSettings(),
      networkPrinters: [],
      printerSettings: {},
    }
  );

  // One-time in-place migration of the legacy 2-destination keys. The map's
  // presence is the migrated marker.
  {
    const legacy = persistedSettings.value as ILegacyPersistedPrintingSettings;

    // Pre-network persisted payloads lack the array (useLocalStorage doesn't
    // merge defaults into existing stored values).
    if (!Array.isArray(legacy.networkPrinters)) {
      legacy.networkPrinters = [];
    }

    if (!legacy.comandaPrinterNamesByStation) {
      legacy.comandaPrinterNamesByStation = {
        1: legacy.comandaCajaPrinterName ?? null,
        2: legacy.comandaCocinaPrinterName ?? null,
      };
    }

    if (
      'comandaCajaPrinterName' in legacy ||
      'comandaCocinaPrinterName' in legacy
    ) {
      delete legacy.comandaCajaPrinterName;
      delete legacy.comandaCocinaPrinterName;
      // Reassign so useLocalStorage persists the key deletions too.
      persistedSettings.value = { ...legacy } as IPersistedPrintingSettings;
    }
  }

  const printers = ref<IPrinter[] | null>(null);
  /** Saved TCP network printers (native strategy only). */
  const networkPrinters = ref<INetworkPrinterConfig[]>(
    persistedSettings.value.networkPrinters ?? []
  );

  /**
   * Migration seed: while `printerSettings` is absent from the persisted
   * payload, derive it from the PERSISTED assignment names (live refs are
   * still null at init) + destination encodings + legacy stickerSettings.
   * Pure and IDEMPOTENT — it re-runs on every load until the first natural
   * persist() commits the map (no forced persist on boot). A rollback's
   * persist() drops the map (old builds strip unknown fields); this re-seeds
   * it on re-upgrade.
   */
  function derivePrinterSettingsSeed(
    persisted: IPersistedPrintingSettings
  ): Record<string, IPersistedPrinterSettings> {
    const seed: Record<string, IPersistedPrinterSettings> = {};

    if (persisted.ticketPrinterName) {
      seed[persisted.ticketPrinterName] = {
        type: 'escpos',
        escpos: { codepage: persisted.ticketPrintingSettings?.encoding },
      };
    }
    const comandaCodepage = persisted.comandaPrintingSettings?.encoding;
    for (const name of Object.values(
      persisted.comandaPrinterNamesByStation ?? {}
    )) {
      if (!name) continue;
      const existing = seed[name];
      if (existing) {
        // One physical printer serving ticket + comanda with DIFFERENT
        // legacy encodings can't be represented in the per-printer model.
        // On conflict the codepage is left UNSET: every document builder
        // passes its destination's `encoding` as printerSettingsOf's
        // fallbackEncoding, so both destinations keep printing EXACTLY
        // their legacy bytes (an explicit winner would garble one of them).
        const ticketCodepage = existing.escpos?.codepage;
        if (comandaCodepage !== ticketCodepage) {
          seed[name] = {
            ...existing,
            escpos: { ...existing.escpos, codepage: undefined },
          };
        }
        continue;
      }
      seed[name] = {
        type: 'escpos',
        escpos: { codepage: comandaCodepage },
      };
    }
    if (persisted.stickerPrinterName) {
      // Type only — DELIBERATELY no `zpl` payload: while it stays absent,
      // printerSettingsOf resolves the legacy localStorage `stickerSettings`
      // at PRINT time, so labels keep their exact pre-upgrade bytes (no
      // offset triplet) and the legacy sticker modal keeps working. The
      // first save in the new ZPL editor materializes an explicit `zpl` and
      // takes over from then on.
      const existing = seed[persisted.stickerPrinterName];
      seed[persisted.stickerPrinterName] = {
        ...(existing ?? {}),
        type: 'zpl',
      };
    }

    return seed;
  }

  /**
   * Per-printer hardware settings (native), keyed by printer name.
   * CRITICAL aliasing invariants (same trap as `networkPrinters`, see
   * persist()): hydrate with a DEEP CLONE of the persisted map and update
   * this ref IMMUTABLY (new objects per write, never in-place nested
   * mutation) — aliasing the persisted payload would make both sides of
   * persist()'s fixed-point compare change together and edits would
   * silently never persist.
   */
  const printerSettings = ref<Record<string, IPersistedPrinterSettings>>(
    (() => {
      const persisted = persistedSettings.value.printerSettings;
      if (persisted && typeof persisted === 'object') {
        return JSON.parse(JSON.stringify(persisted)) as Record<
          string,
          IPersistedPrinterSettings
        >;
      }
      return derivePrinterSettingsSeed(persistedSettings.value);
    })()
  );
  const stickerPrinter = ref<IPrinter | null>(null);
  const comandaPrintersByStation = ref<Record<number, IPrinter | null>>({});
  const ticketPrinter = ref<IPrinter | null>(null);
  const stickerPrintingSettings = reactive({
    ...persistedSettings.value.stickerPrintingSettings,
  });
  const comandaPrintingSettings = reactive({
    ...persistedSettings.value.comandaPrintingSettings,
  });
  const ticketPrintingSettings = reactive({
    ...persistedSettings.value.ticketPrintingSettings,
  });
  const strategy = shallowRef<IPrintingStrategy>(
    markRaw(
      // Android: new APKs (EscPosPrinter plugin) use the V2 native strategy;
      // legacy shells (old APKs) keep printing through their bundled cordova
      // ThermalPrinter plugin until the APK is updated. iOS (Capacitor, not
      // Android) gets the inert plugin strategy: _initStrategy never connects
      // it, so it stays a harmless placeholder.
      Globals.isCapacitorAndroid
        ? Globals.isLegacyAndroidShell
          ? printingStrategyNativeLegacy()
          : printingStrategyNative()
        : printingStrategyPlugin()
    )
  );
  // Native: hydrated from the persisted metadata-only log (bytes stripped —
  // restored jobs render but can't reprint). Hydration bypasses addPrintJob
  // on purpose: historical jobs must NOT re-feed the print-health alarms.
  const lastPrintJobs = ref<IPrintJob[]>(
    Globals.isCapacitorAndroid ? readJobsLog() : []
  );

  /**
   * Bumped every time an auto-print leaves intended items unmarked (retryable
   * failure). The catch-up in use-orders.ts freezes its sync point while this
   * changes between reconcile passes, so the fetch window keeps growing until
   * a pass completes with no such failure.
   */
  const autoPrintRetryableFailureEpoch = ref(0);

  const pluginVersion = ref<string | null>(null);

  const bridgeMode = ref<BridgeMode>('unknown');
  const bridgeIdentity = shallowRef<IBridgeIdentity | null>(null);
  /**
   * v2 (bridge mode) per-printer hardware settings, keyed by bridge printer
   * ref (= IPrinter.id). Written ONLY by the v2 store's legacy projection;
   * wins over the destination settings' encoding in the document builders.
   */
  const printerSettingsByRef = ref<Record<string, IPrinterSettingsEntry>>({});

  /** ALL strategy swaps go through here (keeps setStore wiring consistent). */
  function setStrategy(s: IPrintingStrategy) {
    strategy.value = markRaw(s);
    s.setStore(usePrintingStore());
  }

  /**
   * Correctly-typed strategy accessor: the strategy object is markRaw'd, so
   * its nested refs stay refs at runtime, but Pinia's UnwrapRef store typing
   * would claim otherwise on direct `store.strategy` property access. Action
   * return types are preserved. Reading it inside a watch getter still tracks
   * the strategy ref (swap-safe).
   */
  function getStrategy(): IPrintingStrategy {
    return strategy.value;
  }

  function setBridgeMode(val: BridgeMode) {
    bridgeMode.value = val;
  }

  function setBridgeIdentity(val: IBridgeIdentity | null) {
    bridgeIdentity.value = val;
  }

  function setPrinters(val: IPrinter[] | null) {
    printers.value = val;

    // In bridge mode the v2 sync layer is the sole writer of printer
    // assignments — the v1 name-based re-resolution must not race it.
    if (bridgeMode.value === 'bridge') return;

    if (val) {
      if (persistedSettings.value.stickerPrinterName) {
        const printer = val.find(
          (p) => p.name === persistedSettings.value.stickerPrinterName
        );
        if (printer) {
          stickerPrinter.value = printer;
        }
      }

      const namesByStation =
        persistedSettings.value.comandaPrinterNamesByStation ?? {};
      for (const key of Object.keys(namesByStation)) {
        const stationId = +key;
        const name = namesByStation[stationId];
        if (!name) continue;

        const printer = val.find((p) => p.name === name);
        if (printer) {
          comandaPrintersByStation.value = {
            ...comandaPrintersByStation.value,
            [stationId]: printer,
          };
        }
      }

      if (persistedSettings.value.ticketPrinterName) {
        const printer = val.find(
          (p) => p.name === persistedSettings.value.ticketPrinterName
        );
        if (printer) {
          ticketPrinter.value = printer;
        }
      }
    }
  }

  /**
   * Adds a saved network printer. Names must be unique across BOTH saved
   * network printers AND currently discovered USB/BT devices: assignment
   * re-resolution is first-match-by-name over the merged list, so a
   * collision would make routing nondeterministic. Throws on collision.
   */
  function addNetworkPrinter(config: INetworkPrinterConfig) {
    const name = config.name.trim();
    if (!name) {
      throw new Error('El nombre no puede estar vacío');
    }

    const takenNames = new Set<string>([
      ...networkPrinters.value.map((np) => np.name),
      ...(printers.value ?? []).map((p) => p.name),
    ]);
    // Persisted assignment names of undiscovered (ghost) printers too:
    // matching one would silently take over its assignments via
    // first-match-by-name re-resolution on the next setPrinters (mirrors
    // updateNetworkPrinter's check).
    const persisted = persistedSettings.value;
    for (const assignedName of [
      persisted.ticketPrinterName,
      persisted.stickerPrinterName,
      ...Object.values(persisted.comandaPrinterNamesByStation ?? {}),
    ]) {
      if (assignedName) takenNames.add(assignedName);
    }
    if (takenNames.has(name)) {
      throw new Error(`Ya existe una impresora llamada "${name}"`);
    }

    // host:port doubles as the merged list's IPrinter.id, so a duplicate
    // address would make the second printer unassignable (id-based selects
    // always resolve the first match).
    const address = `${config.host}:${config.port}`;
    if (
      networkPrinters.value.some((np) => `${np.host}:${np.port}` === address)
    ) {
      throw new Error(`Ya existe una impresora con la dirección ${address}`);
    }

    networkPrinters.value = [...networkPrinters.value, { ...config, name }];

    // Keep the discovered list self-consistent without waiting for the next
    // reloadAllPrinters pass.
    if (printers.value) {
      printers.value = [
        ...printers.value,
        { type: 'tcp', id: `${config.host}:${config.port}`, name },
      ];
    }
  }

  /**
   * Removes a saved network printer AND clears every assignment referencing
   * it in the same mutation — setPrinters never clears stale assignments and
   * persist() deliberately retains names for undiscovered printers, so
   * without this the removed printer's assignment would dangle forever.
   */
  function removeNetworkPrinter(name: string) {
    networkPrinters.value = networkPrinters.value.filter(
      (np) => np.name !== name
    );

    if (ticketPrinter.value?.name === name) ticketPrinter.value = null;
    if (stickerPrinter.value?.name === name) stickerPrinter.value = null;

    const byStation = { ...comandaPrintersByStation.value };
    let stationsChanged = false;
    for (const key of Object.keys(byStation)) {
      if (byStation[+key]?.name === name) {
        byStation[+key] = null;
        stationsChanged = true;
      }
    }
    if (stationsChanged) comandaPrintersByStation.value = byStation;

    // The printer's hardware settings go with it, in the same mutation
    // (this direct persisted write is one of the two sanctioned bypasses of
    // persist(); the map must be carried inside it atomically).
    const cleanedSettings = { ...printerSettings.value };
    delete cleanedSettings[name];
    printerSettings.value = cleanedSettings;

    // Persisted *PrinterName entries too: name retention would otherwise
    // resurrect the assignment on the next persist().
    const persisted = persistedSettings.value;
    const namesByStation = {
      ...(persisted.comandaPrinterNamesByStation ?? {}),
    };
    for (const key of Object.keys(namesByStation)) {
      if (namesByStation[+key] === name) namesByStation[+key] = null;
    }
    persistedSettings.value = {
      ...persisted,
      ticketPrinterName:
        persisted.ticketPrinterName === name
          ? null
          : persisted.ticketPrinterName,
      stickerPrinterName:
        persisted.stickerPrinterName === name
          ? null
          : persisted.stickerPrinterName,
      comandaPrinterNamesByStation: namesByStation,
      networkPrinters: [...networkPrinters.value],
      // Detached clone: persisted state must never alias the live ref.
      printerSettings: JSON.parse(JSON.stringify(cleanedSettings)),
    };

    if (printers.value) {
      printers.value = printers.value.filter(
        (p) => !(p.type === 'tcp' && p.name === name)
      );
    }
  }

  /**
   * Atomically renames and/or re-addresses a saved network printer. Rename
   * and re-address share the same re-pointing problem as removal: live
   * assignment refs hold the old IPrinter (stale host:port id) and persist()
   * deliberately retains old names, so BOTH must be updated in the same
   * mutation or the assignment dangles/resurrects (see removeNetworkPrinter).
   * Throws on collision with addNetworkPrinter's Spanish error style.
   */
  function updateNetworkPrinter(
    originalName: string,
    patch: { name: string; host: string; port: number }
  ) {
    const index = networkPrinters.value.findIndex(
      (np) => np.name === originalName
    );
    if (index === -1) {
      throw new Error(`No existe una impresora llamada "${originalName}"`);
    }
    const current = networkPrinters.value[index];

    const name = patch.name.trim();
    if (!name) {
      throw new Error('El nombre no puede estar vacío');
    }

    if (name !== originalName) {
      const takenNames = new Set<string>([
        ...networkPrinters.value.map((np) => np.name),
        ...(printers.value ?? []).map((p) => p.name),
      ]);
      // Persisted assignment names of undiscovered (ghost) printers too:
      // matching one would silently take over its assignments via
      // first-match-by-name re-resolution on the next setPrinters.
      const persisted = persistedSettings.value;
      for (const assignedName of [
        persisted.ticketPrinterName,
        persisted.stickerPrinterName,
        ...Object.values(persisted.comandaPrinterNamesByStation ?? {}),
      ]) {
        if (assignedName) takenNames.add(assignedName);
      }
      takenNames.delete(originalName);
      if (takenNames.has(name)) {
        throw new Error(`Ya existe una impresora llamada "${name}"`);
      }
    }

    const address = `${patch.host}:${patch.port}`;
    if (
      networkPrinters.value.some(
        (np) => np.name !== originalName && `${np.host}:${np.port}` === address
      )
    ) {
      throw new Error(`Ya existe una impresora con la dirección ${address}`);
    }

    const addressChanged =
      current.host !== patch.host || current.port !== patch.port;

    const nextConfigs = [...networkPrinters.value];
    nextConfigs[index] = {
      name,
      host: patch.host,
      port: patch.port,
      // Address changed → the old probe result is meaningless; caller
      // re-probes. The strategy's networkPrinters watcher evicts the cached
      // handle either way.
      supportsDleEot: addressChanged ? false : current.supportsDleEot,
    };
    networkPrinters.value = nextConfigs;

    // Re-point live assignment refs regardless of what changed: they hold
    // the old IPrinter whose host:port id would keep routing to the old
    // address until the next reloadAllPrinters.
    const updatedPrinter: IPrinter = { type: 'tcp', id: address, name };
    if (ticketPrinter.value?.name === originalName) {
      ticketPrinter.value = updatedPrinter;
    }
    if (stickerPrinter.value?.name === originalName) {
      stickerPrinter.value = updatedPrinter;
    }
    const byStation = { ...comandaPrintersByStation.value };
    let stationsChanged = false;
    for (const key of Object.keys(byStation)) {
      if (byStation[+key]?.name === originalName) {
        byStation[+key] = updatedPrinter;
        stationsChanged = true;
      }
    }
    if (stationsChanged) comandaPrintersByStation.value = byStation;

    // Persisted *PrinterName entries in the same mutation: persist()'s name
    // retention would otherwise resurrect the old name while the live ref
    // is unresolved (e.g. right after an app restart).
    if (name !== originalName) {
      // Re-key the printer's hardware settings atomically with the rename
      // (this direct persisted write is one of the two sanctioned bypasses
      // of persist(); the map must be carried inside it) and update the
      // in-memory ref in the same tick.
      const currentSettings = printerSettings.value;
      if (currentSettings[originalName]) {
        const rekeyed = { ...currentSettings };
        rekeyed[name] = rekeyed[originalName];
        delete rekeyed[originalName];
        printerSettings.value = rekeyed;
      }

      const persisted = persistedSettings.value;
      const namesByStation = {
        ...(persisted.comandaPrinterNamesByStation ?? {}),
      };
      for (const key of Object.keys(namesByStation)) {
        if (namesByStation[+key] === originalName) namesByStation[+key] = name;
      }
      persistedSettings.value = {
        ...persisted,
        ticketPrinterName:
          persisted.ticketPrinterName === originalName
            ? name
            : persisted.ticketPrinterName,
        stickerPrinterName:
          persisted.stickerPrinterName === originalName
            ? name
            : persisted.stickerPrinterName,
        comandaPrinterNamesByStation: namesByStation,
        networkPrinters: [...networkPrinters.value],
        // Detached clone: persisted state must never alias the live ref.
        printerSettings: JSON.parse(JSON.stringify(printerSettings.value)),
      };
    }

    // Keep the discovered list self-consistent without waiting for the next
    // reloadAllPrinters pass.
    if (printers.value) {
      printers.value = printers.value.map((p) =>
        p.type === 'tcp' && p.name === originalName
          ? { type: 'tcp', id: address, name }
          : p
      );
    }
  }

  /**
   * Durably unassigns a destination: nulls BOTH the live ref AND the
   * persisted *PrinterName entry in one mutation. Required because
   * set*Printer(null) alone never sticks — persist()'s name retention keeps
   * the persisted name and setPrinters re-resolves it on the next discovery
   * pass, resurrecting the assignment.
   */
  function clearAssignment(
    destination: 'ticket' | 'sticker' | 'comanda',
    stationId?: number
  ) {
    const persisted = persistedSettings.value;

    if (destination === 'ticket') {
      ticketPrinter.value = null;
      persistedSettings.value = { ...persisted, ticketPrinterName: null };
      recomputeLegacyDestinationEncodings();
      return;
    }

    if (destination === 'sticker') {
      stickerPrinter.value = null;
      persistedSettings.value = { ...persisted, stickerPrinterName: null };
      recomputeLegacyDestinationEncodings();
      return;
    }

    if (stationId === undefined) {
      throw new Error('stationId es requerido para desasignar una comanda');
    }
    comandaPrintersByStation.value = {
      ...comandaPrintersByStation.value,
      [stationId]: null,
    };
    persistedSettings.value = {
      ...persisted,
      comandaPrinterNamesByStation: {
        ...(persisted.comandaPrinterNamesByStation ?? {}),
        [stationId]: null,
      },
    };
    recomputeLegacyDestinationEncodings();
  }

  /**
   * Persisted assignment names, exposed read-only for ghost-card derivation,
   * replaces-warnings and name-collision validation in the native printing
   * UI. Live refs can be null for an undiscovered printer while its
   * persisted name is retained — this is the only window into that state.
   */
  const persistedAssignmentNames = computed(() => ({
    ticketPrinterName: persistedSettings.value.ticketPrinterName,
    stickerPrinterName: persistedSettings.value.stickerPrinterName,
    comandaPrinterNamesByStation: {
      ...(persistedSettings.value.comandaPrinterNamesByStation ?? {}),
    },
  }));

  /** Legacy localStorage stickerSettings → normalized ZPL settings. */
  function readLegacyStickerZpl(): IZplPrinterSettings {
    try {
      const raw = localStorage.getItem('stickerSettings');
      return stickerToZplSettings(raw ? JSON.parse(raw) : null);
    } catch (e) {
      console.error('[Printing] could not read legacy stickerSettings', e);
      return stickerToZplSettings(null);
    }
  }

  /**
   * THE per-printer settings resolver used by every document renderer.
   * Desktop (bridge mode) reads the v2 projection (`printerSettingsByRef`,
   * keyed by ref = IPrinter.id); native reads the persisted per-name map.
   * Both return complete normalized shapes.
   *
   * Codepage fallback chain (native): explicit → destination `encoding`
   * (pass the destination's settings encoding as `fallbackEncoding`) →
   * 'cp858'. ZPL fallback chain (both): explicit → legacy localStorage
   * stickerSettings (resolved at PRINT time — the legacy sticker modal must
   * keep affecting printers without explicit zpl settings) → defaults.
   */
  function printerSettingsOf(
    printer: IPrinter | null | undefined,
    fallbackEncoding?: string
  ): {
    escpos: IEscposPrinterSettings;
    zpl: IZplPrinterSettings;
    /**
     * True only when `zpl` came from an explicit per-printer entry (a save
     * in the new ZPL editor / desktop wizard) — legacy-resolved settings
     * must NOT trigger new byte-level behaviors like the offset triplet.
     */
    zplExplicit: boolean;
  } {
    const entry: IPrinterSettingsEntry | IPersistedPrinterSettings | undefined =
      bridgeMode.value === 'bridge'
        ? printer
          ? printerSettingsByRef.value[printer.id]
          : undefined
        : printer
        ? printerSettings.value[printer.name]
        : undefined;

    const escpos = normalizeEscposSettings(entry?.escpos ?? null);
    if (!entry?.escpos?.codepage && fallbackEncoding) {
      escpos.codepage = normalizeEscposSettings({
        codepage: fallbackEncoding,
      }).codepage;
    }

    const zplExplicit = !!entry?.zpl;
    const zpl = entry?.zpl
      ? normalizeZplSettings(entry.zpl)
      : readLegacyStickerZpl();

    return { escpos, zpl, zplExplicit };
  }

  /**
   * THE writer for one printer's persisted hardware settings (native).
   * Immutable update (persist()'s fixed-point guard requires it) followed by
   * the deterministic legacy recompute.
   */
  function setPrinterSettings(name: string, value: IPersistedPrinterSettings) {
    printerSettings.value = {
      ...printerSettings.value,
      // Detach from the caller's object so later caller mutations can't
      // alias store state.
      [name]: JSON.parse(JSON.stringify(value)) as IPersistedPrinterSettings,
    };
    recomputeLegacyDestinationEncodings();
  }

  /** Explicit per-printer codepage, whitelisted; null when unset/invalid. */
  function _explicitCodepageOf(name: string | null | undefined): string | null {
    if (!name) return null;
    const explicit = printerSettings.value[name]?.escpos?.codepage;
    if (typeof explicit !== 'string' || !explicit) return null;
    return normalizeEscposSettings({ codepage: explicit }).codepage;
  }

  /**
   * Deterministic recompute of the legacy destination fields as a pure
   * function of current state (NOT event-driven last-write-wins): ticket /
   * sticker `encoding` ← their assigned printer's codepage; the SINGLE
   * SHARED comanda settings object ← the codepage of the printer assigned
   * to the LOWEST-NUMBERED station (the exact desktop projectToLegacy
   * rule). Also mirrors the sticker printer's resolved zpl settings into
   * legacy localStorage `stickerSettings` so legacy consumers
   * (StickerPrintModal / StickerManager / QrPage / RestaurantTables, which
   * still read LabelPrinter.loadSettings()) render identically and labels
   * stay correct after a rollback. These legacy fields are rollback/compat
   * shims — live read paths use the per-printer map via printerSettingsOf.
   */
  function recomputeLegacyDestinationEncodings() {
    // Bridge mode: the v2 store's projection owns every one of these fields.
    if (bridgeMode.value === 'bridge') return;

    const ticketName =
      ticketPrinter.value?.name ?? persistedSettings.value.ticketPrinterName;
    const ticketCodepage = _explicitCodepageOf(ticketName);
    if (ticketCodepage) ticketPrintingSettings.encoding = ticketCodepage;

    const namesByStation: Record<number, string | null> = {
      ...(persistedSettings.value.comandaPrinterNamesByStation ?? {}),
    };
    for (const key of Object.keys(comandaPrintersByStation.value)) {
      const live = comandaPrintersByStation.value[+key];
      if (live) namesByStation[+key] = live.name;
    }
    const lowestStation = Object.keys(namesByStation)
      .map(Number)
      .filter((sid) => !!namesByStation[sid])
      .sort((a, b) => a - b)[0];
    const comandaCodepage =
      lowestStation !== undefined
        ? _explicitCodepageOf(namesByStation[lowestStation])
        : null;
    if (comandaCodepage) comandaPrintingSettings.encoding = comandaCodepage;

    const stickerName =
      stickerPrinter.value?.name ?? persistedSettings.value.stickerPrinterName;
    const stickerCodepage = _explicitCodepageOf(stickerName);
    if (stickerCodepage) stickerPrintingSettings.encoding = stickerCodepage;

    const stickerZpl = stickerName
      ? printerSettings.value[stickerName]?.zpl
      : undefined;
    if (stickerZpl) {
      try {
        const mirrored = JSON.stringify(
          zplSettingsToSticker(normalizeZplSettings(stickerZpl))
        );
        if (localStorage.getItem('stickerSettings') !== mirrored) {
          localStorage.setItem('stickerSettings', mirrored);
        }
      } catch (e) {
        console.error('[Printing] could not mirror stickerSettings', e);
      }
    }
  }

  /**
   * The ONLY writer of supportsDleEot (add-time probe, modal-open refresh,
   * per-printer test). The native strategy's watcher on networkPrinters
   * evicts cached handles when this changes, so a flag flip takes effect on
   * the next job without an app restart.
   */
  function setNetworkPrinterDleEot(name: string, supportsDleEot: boolean) {
    const index = networkPrinters.value.findIndex((np) => np.name === name);
    if (index === -1) return;
    if (networkPrinters.value[index].supportsDleEot === supportsDleEot) return;

    const next = [...networkPrinters.value];
    next[index] = { ...next[index], supportsDleEot };
    networkPrinters.value = next;
  }

  function setStickerPrinter(val: IPrinter | null) {
    console.log('setStickerPrinter', val);
    stickerPrinter.value = val;
    recomputeLegacyDestinationEncodings();
  }

  function setComandaPrinter(stationId: number, val: IPrinter | null) {
    comandaPrintersByStation.value = {
      ...comandaPrintersByStation.value,
      [stationId]: val,
    };
    recomputeLegacyDestinationEncodings();
  }

  function setTicketPrinter(val: IPrinter | null) {
    console.log('setTicketPrinter', val);
    ticketPrinter.value = val;
    recomputeLegacyDestinationEncodings();
  }

  function setPluginVersion(val: string | null) {
    pluginVersion.value = val;
  }

  function addPrintJob(job: Omit<IPrintJob, 'jobId'>) {
    const record: IPrintJob = { ...job, jobId: uuidv4() };
    // Freeze the (never-mutated) byte payload: Vue 2.7 skips frozen values
    // in both observe() and deep-watch traverse(), so the store-wide persist
    // watcher doesn't walk up to 50 multi-KB arrays on every store flush.
    if (record.data) Object.freeze(record.data);
    // Newest first, rolling cap (the cap also bounds byte memory: `data`
    // lives ON the record and is evicted with it).
    lastPrintJobs.value = [record, ...lastPrintJobs.value].slice(
      0,
      JOBS_LOG_MAX
    );
    // Native: mirror the metadata-only log so the Jobs tab survives an app
    // restart (separate localStorage key — persist()'s fixed-point guard on
    // `printing-settings` stays untouched).
    if (Globals.isCapacitorAndroid) writeJobsLog(lastPrintJobs.value);
    // THE funnel: every recorded outcome (success or failure, every
    // strategy) also feeds the print-health store (app-bar widget).
    usePrintHealthStore().onPrintJob(record);
  }

  function clearPrintJobs() {
    lastPrintJobs.value = [];
    if (Globals.isCapacitorAndroid) clearJobsLog();
  }

  function persist() {
    let data: IPersistedPrintingSettings;

    // Bridge mode: the runtime printer objects hold v2 logical labels, NOT v1
    // spooler names — persisting them would silently lose every assignment on
    // a rollback to the legacy plugin. Keep the previously persisted names
    // verbatim (the settings objects still persist normally).
    if (bridgeMode.value === 'bridge') {
      data = {
        stickerPrinterName: persistedSettings.value.stickerPrinterName,
        comandaPrinterNamesByStation: {
          ...(persistedSettings.value.comandaPrinterNamesByStation ?? {}),
        },
        ticketPrinterName: persistedSettings.value.ticketPrinterName,
        stickerPrintingSettings,
        comandaPrintingSettings,
        ticketPrintingSettings,
        // Native-only data, but it must survive a desktop bridge session on
        // the same browser profile.
        networkPrinters: [...networkPrinters.value],
        printerSettings: printerSettings.value,
      };
    } else {
      // Name retention: a temporarily undiscovered USB printer must not get
      // its assignment wiped, so a null runtime printer keeps the persisted
      // name.
      const persistedMap =
        persistedSettings.value.comandaPrinterNamesByStation ?? {};
      const comandaPrinterNamesByStation: Record<number, string | null> = {
        ...persistedMap,
      };

      for (const key of Object.keys(comandaPrintersByStation.value)) {
        const stationId = +key;
        comandaPrinterNamesByStation[stationId] =
          comandaPrintersByStation.value[stationId]?.name ??
          persistedMap[stationId] ??
          null;
      }

      data = {
        stickerPrinterName:
          stickerPrinter.value?.name ??
          persistedSettings.value.stickerPrinterName,
        comandaPrinterNamesByStation,
        ticketPrinterName:
          ticketPrinter.value?.name ??
          persistedSettings.value.ticketPrinterName,
        stickerPrintingSettings,
        comandaPrintingSettings,
        ticketPrintingSettings,
        networkPrinters: [...networkPrinters.value],
        printerSettings: printerSettings.value,
      };
    }

    // CRITICAL no-change guard: usePrinting(main) deep-watches the whole
    // store and calls persist() on ANY change — and this ref is itself store
    // state. An unconditional fresh-object write would re-trigger that
    // watcher inside the same flush, ~100 times, until Vue's circular-update
    // breaker ABORTS the flush (observed: stranded vue-js-modal leave
    // transitions — modals stuck open). Skipping identical payloads turns
    // the echo into a fixed point (localStorage serializes to JSON, so JSON
    // equality is exactly "nothing would change on disk").
    const json = JSON.stringify(data);
    if (json === JSON.stringify(persistedSettings.value)) return;

    // Store a DETACHED clone: `data` embeds the live settings reactives, and
    // storing them by reference would alias both sides of the comparison
    // above — an in-place settings mutation would look "unchanged" forever
    // and never reach localStorage.
    persistedSettings.value = JSON.parse(json) as IPersistedPrintingSettings;
  }

  /**
   * Detached deep clone of the full persisted payload. Backup seam
   * (use-printing-backup.ts): callers get a mutable snapshot that can never
   * alias persisted state (see persist()'s fixed-point compare). Reading it
   * inside a watcher getter tracks the whole payload (the stringify walks
   * every nested key), so it doubles as a change source.
   */
  function persistedSettingsSnapshot(): IPersistedPrintingSettings {
    return JSON.parse(
      JSON.stringify(persistedSettings.value)
    ) as IPersistedPrintingSettings;
  }

  /**
   * Restore seam (use-printing-backup.ts, native only): replaces the ENTIRE
   * persisted payload and every in-memory projection of it in one
   * synchronous pass. Caller contract: payload is already SANITIZED and the
   * store is empty (no printers, no assignments) — this is NOT a general
   * setter. Live assignment refs stay null on purpose: the next discovery
   * pass re-resolves them from the restored persisted names (exactly the
   * post-restart hydration path). After this, refs and persisted payload
   * agree, so persist()'s fixed-point guard sees no echo to write.
   */
  function restorePersistedSettings(payload: IPersistedPrintingSettings) {
    // Detached clones on EVERY assignment: persisted payload and live refs
    // must never alias each other or the caller's object.
    persistedSettings.value = JSON.parse(
      JSON.stringify(payload)
    ) as IPersistedPrintingSettings;
    networkPrinters.value = JSON.parse(
      JSON.stringify(payload.networkPrinters ?? [])
    ) as INetworkPrinterConfig[];
    printerSettings.value = JSON.parse(
      JSON.stringify(payload.printerSettings ?? {})
    ) as Record<string, IPersistedPrinterSettings>;
    Object.assign(stickerPrintingSettings, payload.stickerPrintingSettings);
    Object.assign(comandaPrintingSettings, payload.comandaPrintingSettings);
    Object.assign(ticketPrintingSettings, payload.ticketPrintingSettings);
  }

  return {
    printers,
    setPrinters,
    networkPrinters: readonly(networkPrinters),
    addNetworkPrinter,
    removeNetworkPrinter,
    updateNetworkPrinter,
    clearAssignment,
    persistedAssignmentNames,
    setNetworkPrinterDleEot,
    stickerPrinter: readonly(stickerPrinter),
    setStickerPrinter,
    comandaPrintersByStation: readonly(comandaPrintersByStation),
    setComandaPrinter,
    ticketPrinter: readonly(ticketPrinter),
    setTicketPrinter,
    stickerPrintingSettings,
    comandaPrintingSettings,
    ticketPrintingSettings,
    strategy,
    setStrategy,
    getStrategy,
    bridgeMode,
    setBridgeMode,
    bridgeIdentity,
    setBridgeIdentity,
    printerSettingsByRef,
    printerSettings: readonly(printerSettings),
    setPrinterSettings,
    printerSettingsOf,
    recomputeLegacyDestinationEncodings,
    autoPrintRetryableFailureEpoch,
    persist,
    persistedSettingsSnapshot,
    restorePersistedSettings,
    pluginVersion,
    setPluginVersion,
    lastPrintJobs,
    addPrintJob,
    clearPrintJobs,
  };
});

/*if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePwaStore, import.meta.hot));
}*/

/**
 * Per-order auto-print serialization chains. Module-level so every
 * usePrinting() instance shares the same queue.
 */
const orderAutoPrintChains = new Map<number, Promise<boolean>>();

/**
 * Group-payment print aggregator — lazy module-level singleton created by the
 * first usePrinting() call: multiple instances exist app-wide and each has
 * closure-scoped print functions, so a per-instance aggregator would split
 * timers/state. Binding onFlush to the creating instance's closure is fine:
 * every instance reads the same shared Pinia stores.
 */
let groupPaymentAggregator: ReturnType<
  typeof createGroupPaymentAggregator
> | null = null;

/**
 * Safety net: quiet period after the last arrival before a group whose
 * expected members did not all arrive (lost delivery, store guard skipped an
 * evaluation) is flushed anyway (trailing debounce, see
 * createGroupPaymentAggregator).
 */
const GROUP_PAYMENT_DEBOUNCE_MS = 2000;

export function usePrinting(main = false) {
  const store = usePrintingStore();
  const restaurantStore = useRestaurantStore();
  const domain = getTopLevelDomain();

  const menuStore = useMenuStore();

  if (main) store.strategy.setStore(store);

  const {
    stickerPrinter,
    comandaPrintersByStation,
    ticketPrinter,
    printers,
    pluginVersion,
    strategy,
    comandaPrintingSettings,
    stickerPrintingSettings,
    ticketPrintingSettings,
  } = storeToRefs(store);

  const isOutdatedVersion = computed(() => {
    const isBistrify = window.location.host.startsWith('bistrify');
    return (
      pluginVersion.value &&
      (pluginVersion.value < '1.2.0' ||
        (isBistrify && pluginVersion.value < '1.2.3'))
    );
  });

  /** Legacy-plugin session on a desktop browser → nudge to the new bridge. */
  const upgradeAvailable = computed(() =>
    isUpgradeAvailable(store.bridgeMode, Globals.isCapacitorAndroid)
  );

  async function getPrinters(mode: 'usb' | 'bluetooth') {
    try {
      const printers = await store.strategy.getPrinters(mode);
      store.setPrinters(printers);
    } catch (e) {
      console.error(e);
      vueData.addAlert('Error al obtener impresoras', AlertType.Error);
    }
  }

  /**
   * The ONE shared discovery path (status watcher + native settings modal).
   *
   * On the native strategy it fetches usb + bluetooth and appends the
   * persisted network printers, then calls setPrinters ONCE with the merged
   * list — without this, saved BT/network printers never auto-resolve after
   * an app restart and auto-printing to them silently dies (setPrinters
   * replaces the whole list, so a usb-only fetch would drop them).
   *
   * Transports are isolated (allSettled): a Bluetooth rejection must never
   * prevent USB + network resolution (persisted names survive an empty pass
   * thanks to persist()'s name retention). Passive (non-interactive) calls
   * skip Bluetooth entirely unless permission is already granted — startup
   * must never trigger the BT permission dialog or enable prompt.
   *
   * On non-native strategies it degrades to the plain usb fetch.
   */
  async function reloadAllPrinters(
    options: { interactive?: boolean } = {}
  ): Promise<void> {
    const currentStrategy = store.getStrategy();

    if (!isNativePrintingStrategy(currentStrategy)) {
      await getPrinters('usb');
      return;
    }

    const [usbResult, bluetoothResult] = await Promise.allSettled([
      currentStrategy.getPrinters('usb'),
      currentStrategy.listBluetoothPrinters({
        passive: options.interactive !== true,
      }),
    ]);

    if (usbResult.status === 'rejected') {
      console.error('Error listando impresoras USB', usbResult.reason);
    }
    if (bluetoothResult.status === 'rejected') {
      console.error(
        'Error listando impresoras Bluetooth',
        bluetoothResult.reason
      );
    }

    const networkPrinters: IPrinter[] = store.networkPrinters.map((np) => ({
      type: 'tcp' as const,
      id: `${np.host}:${np.port}`,
      name: np.name,
    }));

    store.setPrinters([
      ...(usbResult.status === 'fulfilled' ? usbResult.value : []),
      ...(bluetoothResult.status === 'fulfilled' ? bluetoothResult.value : []),
      ...networkPrinters,
    ]);
  }

  async function printZpl(
    printer: IPrinter,
    zpl: string,
    printContext?: IPrintContext,
    /** Order the label belongs to: lets a success auto-clear the widget's
     * order-keyed sticker failure entry. Label tools print order-less. */
    orderId?: number
  ) {
    function stringToUint8Array(str) {
      const array = new Uint8Array(str.length);
      for (let i = 0; i < str.length; i++) {
        array[i] = str.charCodeAt(i);
      }
      return array;
    }

    const bytes = Array.from(stringToUint8Array(zpl));
    const base64 = uint8ArrayToBase64(bytes);

    try {
      await store.strategy.printRawBytes({
        printer,
        bytes,
        base64,
        printContext,
      });
    } catch (e) {
      // Bytes → the failure's job record (created frames up, where only the
      // error is in scope) so Reintentar/Reimprimir can resubmit them.
      attachFailedJobBytes(e, bytes);
      throw e;
    }

    store.addPrintJob({
      printerType: PrinterType.Etiqueta,
      title: 'Etiqueta',
      printerName: printer.name,
      createdAt: new Date(),
      status: 'printed',
      data: bytes,
      isAutoPrint: !!printContext?.dedupeKey,
      metadata: typeof orderId === 'number' ? { orderId } : undefined,
    });

    return bytes;
  }

  async function printByteArray(
    printer: IPrinter,
    bytes: number[] | Uint8Array,
    printContext?: IPrintContext
  ) {
    if (bytes instanceof Uint8Array) {
      bytes = Array.from(bytes);
    }

    const base64 = uint8ArrayToBase64(bytes);

    try {
      await store.strategy.printRawBytes({
        printer,
        bytes,
        base64,
        printContext,
      });
    } catch (e) {
      // Bytes → the failure's job record (see printZpl).
      attachFailedJobBytes(e, bytes as number[]);
      throw e;
    }

    return bytes as number[];
  }

  /** Port the current bridge strategy is pinned to (bridge mode only). */
  let adoptedBridgePort: number | null = null;

  /**
   * Fires the migration-completed telemetry event. Adoption usually happens
   * on page load, BEFORE the restaurant API response and before App.vue
   * bootstraps vue-gtag (both async) — firing immediately would lose the
   * event or its restaurant attribution, so poll until both are ready.
   */
  function _fireMigratedEvent() {
    const tryFire = (): boolean => {
      const restaurantId = restaurantStore.restaurant?.id;
      const gtagReady =
        typeof (window as Window & { gtag?: unknown }).gtag === 'function';
      if (!restaurantId || !gtagReady) return false;
      try {
        gtagEvent('migrated', {
          event_category: 'print_upgrade',
          event_label: restaurantId,
        });
      } catch {
        // Telemetry must never break printing.
      }
      return true;
    };

    if (tryFire()) return;
    let attempts = 0;
    const timer = setInterval(() => {
      if (tryFire() || ++attempts >= 60) clearInterval(timer);
    }, 1000);
  }

  function _adoptBridge(detected: { identity: IBridgeIdentity; port: number }) {
    // Stop the legacy socket's retry loop before swapping.
    store.getStrategy().disconnect?.();
    adoptedBridgePort = detected.port;

    const bridgeStrategy = printingStrategyBridge(detected);
    store.setBridgeIdentity(detected.identity);
    // Mode flips BEFORE connect(): the legacy-store guards (persist /
    // setPrinters) must already be active when the bridge hydrates.
    store.setBridgeMode('bridge');

    // Upgrade-nudge bookkeeping: a bridge adoption after the interstitial was
    // shown means the user completed the migration.
    try {
      if (consumeUpgradeSeen()) _fireMigratedEvent();
    } catch {
      // Telemetry must never break printing.
    }
    clearSnooze();

    store.setStrategy(bridgeStrategy);
    // Hook the v2 config hydration into the connect sequence BEFORE
    // connecting: status must never flip to connected on unhydrated
    // assignments (Part 0's pipeline-ready trigger fires on it).
    usePrintingV2Store().attachBridge(bridgeStrategy);
    bridgeStrategy.connect();
  }

  /** Fresh read on every call (avoids stale control-flow narrowing). */
  function _isBridgeAdopted() {
    return store.bridgeMode === 'bridge';
  }

  function _connectLegacy() {
    // On non-Capacitor the initial strategy IS the legacy plugin strategy.
    const legacy = store.getStrategy();
    legacy.connect();

    if (isLegacyPluginStrategy(legacy)) {
      watch(
        () => legacy.pluginInfo.value,
        async (info) => {
          if (!info || _isBridgeAdopted()) return;

          // protocol >= 2 means we reached the NEW bridge's legacy emulation
          // on 3030 — the identity probe must have raced/failed; prefer the
          // real bridge protocol.
          if ((info.protocol ?? 1) >= 2) {
            const found = await detectBridge();
            if (found && !_isBridgeAdopted()) {
              _adoptBridge(found);
              return;
            }
          }

          store.setBridgeMode('legacy');
        },
        { immediate: true }
      );
    }

    // 'none' until something answers (drives the install CTA in the V2 modal).
    setTimeout(() => {
      if (store.bridgeMode === 'unknown') store.setBridgeMode('none');
    }, 5000);
  }

  async function _initStrategy() {
    // Android (new APK → V2 native strategy; legacy shell → cordova
    // ThermalPrinter fallback): both connect instantly, then the Connected
    // watcher below runs the startup USB discovery.
    if (Globals.isCapacitorAndroid) {
      store.getStrategy().connect();
      return;
    }

    // iOS (Tier A): no printing at all — no bridge detection, no legacy
    // probe, no re-probe interval (an iOS build must not probe localhost for
    // a desktop bridge forever). bridgeMode stays 'unknown' and
    // deviceCanEverPrint === false already hides all printing UI.
    if (Globals.isCapacitor) return;

    const detected = await detectBridge();
    if (detected) {
      _adoptBridge(detected);
    } else {
      _connectLegacy();
    }

    // Installing the bridge mid-session must flip the UI live: while nothing
    // is connected, re-probe every 30s. In bridge mode the WS client
    // reconnects on its own against the ADOPTED port — but a daemon restart
    // can land on a different port (its previous one taken), so a
    // disconnected bridge session must also re-detect and re-adopt on moves.
    let probing = false;
    setInterval(async () => {
      if (probing) return;
      if (store.getStrategy().status.value === PrintPluginStatus.Connected)
        return;

      probing = true;
      try {
        const found = await detectBridge();
        if (!found) return;
        if (!_isBridgeAdopted()) {
          _adoptBridge(found);
        } else if (found.port !== adoptedBridgePort) {
          _adoptBridge(found);
        }
      } finally {
        probing = false;
      }
    }, 30000);
  }

  if (main) {
    watch(store, (val) => {
      store.persist();
    });

    // Getter source (NOT a captured ref): re-tracks after a strategy swap.
    watch(
      () => store.getStrategy().status.value,
      (val: PrintPluginStatus) => {
        if (val === PrintPluginStatus.Connected) {
          // Passive: startup discovery must never prompt for permissions.
          void reloadAllPrinters();
        }
      },
      { immediate: true }
    );

    void _initStrategy();
  }

  function markPrinted({
    order,
    indexes,
    amounts,
    printerType,
  }: {
    order: IOrder;
    indexes: number[];
    amounts: number[];
    printerType: PrinterType;
  }) {
    if (Globals.IsLocalhost) {
      console.log('markPrinted', {
        order: order.id,
        indexes,
        amounts,
        printerType,
      });
    }

    // Central printed-while-paid stamp hook: any ticket mark executed from a
    // Paid snapshot stamps the order-level marker (OnPaid trigger dedupe).
    // Covers per-order auto prints, the group flush's markAllMembers, manual
    // prints, and the unsafe-failure "mark anyway" paths. The summary variant
    // never marks, hence never stamps.
    const printedPaid =
      printerType === PrinterType.Ticket &&
      order.paymentStatus === OrderPaymentStatus.Paid;
    // Captured BEFORE the in-place stamp below (ordering matters: checking
    // after would make marker-only sends impossible).
    const wasAlreadyStamped = !!order.printedPaidAt;

    if (printedPaid) {
      // In-place optimistic stamp: queued serialized evaluations on the same
      // instance must gate immediately (mirrors the in-place counter bumps).
      order.printedPaidAt ??= new Date().toISOString();
    }

    // Marker-only request (empty indexes) when a fresh stamp must reach the
    // server, e.g. the manual group print of paid orders.
    if (!indexes.length && !(printedPaid && !wasAlreadyStamped)) return;

    let key: 'printT' | 'printC' | 'printE';

    switch (printerType) {
      case PrinterType.Ticket:
        key = 'printT';
        break;
      case PrinterType.Comanda:
        key = 'printC';
        break;
      case PrinterType.Etiqueta:
        key = 'printE';
        break;
    }

    for (let i = 0; i < indexes.length; i++) {
      const index = indexes[i];
      const orderItem = order.orderItems.find((oI) => oI.index === index);

      if (!orderItem) continue;

      orderItem[key] = amounts[i];
    }

    const payload: any = {
      orderId: order.id,
      indexes,
      [key]: amounts,
    };

    if (printedPaid) {
      payload.printedPaid = true;
    }

    const printedStore = usePrintedStore();
    printedStore.addPrinted(payload);

    OrdersService.v2MarkPrinted(payload).catch((e) => {
      console.error('Error marcando como impreso', e);
      printedStore.queuePending(payload);
    });
  }

  /**
   * Records that an auto-print left intended items unmarked (retryable
   * failure): bumps the store epoch so the catch-up in use-orders.ts freezes
   * its sync point until a reconcile pass completes with no such failure.
   */
  function _noteAutoPrintRetryableFailure() {
    store.autoPrintRetryableFailureEpoch++;
  }

  /**
   * Whether any auto-print evaluation is currently in flight. The catch-up
   * heartbeat must not advance the sync point while one is: a retry-safe
   * failure can take longer than a heartbeat period to land (bridge safe-retry
   * loop x the drivers' 30s watchdogs), and the epoch only increments when it
   * does.
   */
  function hasInFlightAutoPrintEvaluations(): boolean {
    // The aggregator's pending window (pre-flush debounce) isn't registered
    // in the chains yet; an in-flight flush is covered by both.
    return (
      orderAutoPrintChains.size > 0 ||
      (groupPaymentAggregator?.hasPending() ?? false)
    );
  }

  /**
   * Auto-print decision layer. STATE-BASED (level triggers over the order's
   * current state), not edge-based: it can be safely re-run over the same
   * order any number of times because idempotency comes from the per-item
   * printed counters (printT/printC/printE) via printMode 'unprinted'.
   * Called by both the live WS path (onOrderChange) and catch-up
   * reconciliation. Evaluations are serialized per order so concurrent events
   * (two WS events, WS + catch-up) queue instead of racing.
   *
   * @returns whether any print output was produced for this order.
   */
  async function evaluateOrderAutoPrint(
    order: IOrder,
    groupPayment?: IGroupPaymentInfo
  ): Promise<boolean> {
    const previous = orderAutoPrintChains.get(order.id) ?? Promise.resolve();
    const current = previous
      .catch(() => undefined)
      .then(() => _evaluateOrderAutoPrint(order, groupPayment));

    orderAutoPrintChains.set(order.id, current);

    try {
      return await current;
    } finally {
      if (orderAutoPrintChains.get(order.id) === current) {
        orderAutoPrintChains.delete(order.id);
      }
    }
  }

  async function _evaluateOrderAutoPrint(
    order: IOrder,
    groupPayment?: IGroupPaymentInfo
  ): Promise<boolean> {
    if (strategy.value.status.value !== PrintPluginStatus.Connected) {
      // A mid-reconcile disconnect silently skips the pass's remaining
      // evaluations; without this bump the pass would look clean and advance
      // the sync point. Spurious bumps while disconnected are harmless
      // (nothing advances while the pipeline is down) and self-heal on the
      // next clean pass.
      _noteAutoPrintRetryableFailure();
      return false;
    }

    if (!isOrderAutoPrintable(order)) return false;

    // A serialized evaluation may start on a payload snapshotted BEFORE the
    // previous evaluation (e.g. catch-up) marked items: re-merge the printed
    // cache so the trigger matrix sees the latest counters. In legacy mode
    // there is no bridge-side dedupe to catch the resulting double print.
    usePrintedStore().mergeIntoOrder(order);

    // Orders paid together print ONE combined ticket: register THIS delivery
    // with the group aggregator BEFORE any print gate. Every order the payment
    // funded carries the marker — including a member left Awaiting by a
    // partial leg, or one whose receipt already printed — and each one counts
    // toward group.fundedOrderIds; the flush re-classifies members (receipt
    // vs context) in resolveGroupPrintPlan. Only live WS deliveries carry
    // groupPayment (catch-up never defers). A late arrival after the group
    // already flushed (add() → false) falls through to the per-order print.
    const deferredToPaymentGroup =
      !!groupPayment &&
      !!store.ticketPrinter &&
      !!groupPaymentAggregator &&
      shouldDeferToPaymentGroup({
        settings: store.ticketPrintingSettings,
        groupPayment,
      }) &&
      groupPaymentAggregator.add(order, groupPayment);

    let printedAny = false;

    const handlePrinting = async (
      printingSettings: IPrintingSettings,
      printType: PrinterType
    ) => {
      if (printType === PrinterType.Ticket && deferredToPaymentGroup) return;

      if (
        !shouldAutoPrint({
          order,
          settings: printingSettings,
          deviceId: restaurantStore.deviceId,
        })
      )
        return;

      // Ticket mode is order-level for OnPaid: gate on the printed-while-paid
      // marker ('skip') and print ALL items ('all'). Other triggers/types
      // keep counter-based 'unprinted'.
      let printMode: 'unprinted' | 'all' = 'unprinted';
      if (printType === PrinterType.Ticket) {
        const mode = resolveTicketAutoPrintMode({
          settings: printingSettings,
          order,
        });
        if (mode === 'skip') return;
        printMode = mode;
      }

      console.log('print', { printType, printMode });

      try {
        const produced = await printOrder({
          printerType: printType,
          order,
          printMode,
          isAutoPrint: true,
        });
        printedAny = printedAny || produced;
      } catch (e) {
        vueData.addAlert('Error imprimiendo', AlertType.Error);
        console.error('Error imprimiendo', e);
        // Unsafe/unknown failures (bridge or native) marked the items
        // (nothing left to retry). Everything else — safe failures, plain
        // Errors, and StickerBatchError (no own retrySafety, so it lands
        // here too: a sticker batch can leave a remainder of unmarked labels
        // even on unsafe causes) — left items unmarked and must freeze the
        // sync point. ComandaBatchError never reaches this catch: the comanda
        // fan-out absorbs it and folds the remainder into retryableFailure.
        if (!hasUnsafeRetrySafety(e)) {
          _noteAutoPrintRetryableFailure();
        }
      }
    };

    // WP2b: the three destination branches dispatch CONCURRENTLY — a slow or
    // retrying printer must not delay another printer's documents. Both
    // transports already serialize per physical printer (native printChains /
    // bridge per-printer FIFOs), so this only overlaps DIFFERENT printers.
    // `handlePrinting` never rejects (its try/catch folds failures into
    // `printedAny`/epoch effects), so Promise.all preserves today's
    // propagation semantics. NOTE: cross-type submission order on a SHARED
    // physical printer becomes best-effort (the strategy still serializes the
    // bytes; dedupeKeys already differ by printerType so no dedupe collapse)
    // — explicitly non-contractual.
    const branches: Promise<void>[] = [];

    // The group deferral was decided above, before any branch starts; the
    // ticket branch is simply pushed first.
    if (store.ticketPrinter) {
      branches.push(
        handlePrinting(store.ticketPrintingSettings, PrinterType.Ticket)
      );
    }

    if (_hasAnyComandaPrinter()) {
      branches.push(
        handlePrinting(store.comandaPrintingSettings, PrinterType.Comanda)
      );
    }

    if (store.stickerPrinter) {
      branches.push(
        handlePrinting(store.stickerPrintingSettings, PrinterType.Etiqueta)
      );
    }

    await Promise.all(branches);

    return printedAny;
  }

  /** Thin wrapper kept for the live WS path (use-orders.ts). */
  async function onOrderChange({
    order,
    groupPayment,
  }: {
    order: IOrder;
    groupPayment?: IGroupPaymentInfo;
  }) {
    await evaluateOrderAutoPrint(order, groupPayment);
  }

  /**
   * Group-payment flush entry point: serializes the whole flush through the
   * per-order chains of EVERY member (chained behind any in-flight
   * evaluation) so a concurrent per-order evaluation — a second WS update
   * without groupPayment, or a catch-up pass — can never race the group doc
   * into a double print.
   */
  async function _flushGroupPaymentPrints(
    capturedMembers: IOrder[],
    group: IGroupPaymentInfo
  ): Promise<void> {
    // Serialize against EVERY selection member (group.orderIds), not just
    // the captured ones: the flush may promote a non-captured member (e.g. a
    // stale Paid-unstamped order the catch-up reconciler is processing
    // concurrently) to receipt, and racing that evaluation prints it twice.
    // Snapshot-then-register keeps the waits a DAG — no deadlock possible.
    const previous = group.orderIds.map((orderId) =>
      (orderAutoPrintChains.get(orderId) ?? Promise.resolve(false)).catch(
        () => false
      )
    );

    const current: Promise<boolean> = Promise.all(previous).then(async () => {
      await _doGroupPaymentFlush(capturedMembers, group);
      return false;
    });

    for (const orderId of group.orderIds) {
      orderAutoPrintChains.set(orderId, current);
    }

    try {
      await current;
    } finally {
      for (const orderId of group.orderIds) {
        if (orderAutoPrintChains.get(orderId) === current) {
          orderAutoPrintChains.delete(orderId);
        }
      }
    }
  }

  /** The actual group-payment print, run inside the members' chains. */
  async function _doGroupPaymentFlush(
    capturedMembers: IOrder[],
    group: IGroupPaymentInfo
  ): Promise<void> {
    // Preconditions may have changed during the debounce window: bail as a
    // retryable failure (items stay unmarked; catch-up recovers per-order).
    if (
      strategy.value.status.value !== PrintPluginStatus.Connected ||
      !store.ticketPrinter
    ) {
      _noteAutoPrintRetryableFailure();
      return;
    }

    const ordersStore = useOrdersStore();
    const printedStore = usePrintedStore();

    // Full-selection document: resolve every id of the group marker
    // (store-first, captured WS payloads as fallback), classify members into
    // receipt (Paid + unstamped: printed AND marked) vs context (rendered
    // for correct selection totals, never marked).
    const plan = resolveGroupPrintPlan({
      group,
      capturedMembers,
      findLiveOrder: (id) => ordersStore.orders?.find((o) => o.id === id),
      mergePrintedCache: (order) => printedStore.mergeIntoOrder(order),
      getFilteredTicketItems: (order) =>
        _getFilteredItems({
          printerType: PrinterType.Ticket,
          order,
          printMode: 'unprinted',
        }),
      settings: store.ticketPrintingSettings,
      deviceId: restaurantStore.deviceId,
    });

    // No receipt members (replay / duplicate delivery): no print, no marks.
    if (!plan) return;

    // A lone document order degrades to the normal per-order print, which
    // owns its marking policy (incl. unsafe-failure marking). A lone receipt
    // WITH context members must still print the group document (the context
    // renders the selection totals).
    if (plan.docOrders.length === 1) {
      try {
        await printOrder({
          printerType: PrinterType.Ticket,
          order: plan.receipt[0].order,
          printMode: plan.receipt[0].mode,
          isAutoPrint: true,
        });
      } catch (e) {
        vueData.addAlert('Error imprimiendo', AlertType.Error);
        console.error('Error imprimiendo', e);
        if (!hasUnsafeRetrySafety(e)) {
          _noteAutoPrintRetryableFailure();
        }
      }
      return;
    }

    const orders = plan.docOrders;
    const sharedTableName = orders.every(
      (o) => o.tableId && o.tableId === orders[0].tableId
    )
      ? orders[0].table?.name
      : undefined;
    const title = sharedTableName
      ? `mesa ${sharedTableName}`
      : `${orders.length} pedidos`;

    // Marks receipt members ONLY: marking a context member would bump its
    // printT counters (locally, in the printed cache, and server-side),
    // suppressing its legitimate later per-order print and corrupting
    // counter-based dedupe. (The server-side printedPaidAt stamp is a silent
    // no-op on non-Paid orders, not a rejection — never rely on it.)
    const markAllMembers = () => {
      for (const { order, items } of plan.receipt) {
        markPrinted({
          order,
          indexes: items.map((item) => item.index),
          amounts: items.map((item) => item.amount),
          printerType: PrinterType.Ticket,
        });
      }
    };

    try {
      await printGroup(PrinterType.Ticket, title, orders, {
        dedupeKey: plan.dedupeKey,
      });
      markAllMembers();
    } catch (e) {
      vueData.addAlert('Error imprimiendo', AlertType.Error);
      console.error('Error imprimiendo grupo de pedidos pagados', e);

      // Failed jobs must be visible in the recent-jobs UI (success records
      // happen inside printGroup).
      store.addPrintJob({
        printerType: PrinterType.Ticket,
        createdAt: new Date(),
        status: jobStatusOfError(e),
        errorMessage: e instanceof Error ? e.message : String(e),
        retrySafety: retrySafetyOf(e),
        // Rendered bytes when the send (not the render) failed — enables
        // Reimprimir from the Jobs tab.
        data: failedJobBytesOf(e),
        // Group-payment prints are auto-prints (plan.dedupeKey exists):
        // catch-up owns their retry.
        isAutoPrint: true,
        title: `Ticket ${title}`,
        metadata: { orderIds: orders.map((o) => o.id) },
      });

      // Same marking policy as printOrder: on an unsafe/unknown failure the
      // doc MAY have printed, so mark anyway (catch-up must never silently
      // re-print a possibly-printed document). Safe failures and plain
      // Errors leave items unmarked and freeze the sync point so catch-up
      // recovers with per-order tickets.
      if (hasUnsafeRetrySafety(e)) {
        markAllMembers();
      } else {
        _noteAutoPrintRetryableFailure();
      }
    }
  }

  if (!groupPaymentAggregator) {
    groupPaymentAggregator = createGroupPaymentAggregator({
      debounceMs: GROUP_PAYMENT_DEBOUNCE_MS,
      onFlush: (orders, group) => _flushGroupPaymentPrints(orders, group),
      onDroppedPending: () => _noteAutoPrintRetryableFailure(),
    });
  }

  /**
   * Drops pending (unflushed) payment groups. Called on branch switch /
   * socket teardown so a flush never runs against a stale context; dropped
   * members recover per-order via the catch-up reconciler.
   */
  function clearPendingGroupPrints() {
    groupPaymentAggregator?.clear();
  }

  //#region Print methods

  const ordersService = new OrdersService();
  function ensureOrderPrintable(order: IOrder) {
    /*await Promise.all([
      (async () => {
        if (typeof order.orderFields === 'undefined' || force) {
          const nOrder = await OrdersService.getById(order.id);
          order.orderFields = nOrder.orderFields;
        }
      })(),
      (async () => {
        if (!order.orderItems || force) {
          const items = await ordersService.getOrderItems(order.id);
          order.orderItems = items;
        }
      })(),
    ]);*/
    if (typeof order.orderFields === 'undefined') {
      throw new Error('Ocurrió un error al imprimir (fields)');
    }

    if (!order.orderItems) {
      throw new Error('Ocurrió un error al imprimir (items)');
    }
  }

  //#region Print Order Utils
  function _getPrintKey(
    printerType: PrinterType
  ): 'printT' | 'printC' | 'printE' {
    switch (printerType) {
      case PrinterType.Ticket:
        return 'printT';
      case PrinterType.Comanda:
        return 'printC';
      case PrinterType.Etiqueta:
        return 'printE';
    }
  }

  /**
   * Marks units credited as printed (or possibly printed) in THIS run on top
   * of the current counters: `min(amount, counter + credited)` — the sticker
   * batch formula, monotonic by construction (the server keeps GREATEST and
   * the printed store max-merges). Entries at 0 and impossible index misses
   * are skipped, degrading to "not marked", the safe direction.
   */
  function _markCreditedUnits({
    order,
    printerType,
    unitsByIndex,
  }: {
    order: IOrder;
    printerType: PrinterType;
    unitsByIndex: ReadonlyMap<number, number>;
  }) {
    const key = _getPrintKey(printerType);
    const indexes: number[] = [];
    const amounts: number[] = [];

    for (const [index, units] of unitsByIndex) {
      if (units <= 0) continue;
      const original = order.orderItems.find((oi) => oi.index === index);
      if (!original) continue;
      indexes.push(index);
      amounts.push(Math.min(original.amount, (original[key] ?? 0) + units));
    }

    if (indexes.length) markPrinted({ order, indexes, amounts, printerType });
  }

  function _getFilteredItems({
    printerType,
    order,
    printMode,
  }: {
    printerType: PrinterType;
    order: IOrder;
    printMode?: 'all' | 'unprinted' | 'unprintedOrAll';
  }) {
    if (printMode === 'all') {
      return order.orderItems;
    }

    const originalItems = order.orderItems;

    const orderItemsAmount = order.orderItems.reduce(
      (acc, item) => acc + item.amount,
      0
    );

    const printKey = _getPrintKey(printerType);

    const printedAmount = order.orderItems.reduce(
      (acc, item) => acc + item[printKey],
      0
    );

    if (printedAmount >= orderItemsAmount)
      return printMode === 'unprinted' ? [] : originalItems;

    // If ticket, either all or none
    if (printerType === PrinterType.Ticket) {
      if (printMode == 'unprinted' && printedAmount > 0) {
        return [];
      } else {
        return originalItems;
      }
    }

    const orderItemsToPrint: IOrderItem[] = [];

    for (const item of originalItems) {
      if (item.canceledAt) continue;

      const existingCount = item[printKey];

      if (existingCount >= item.amount) continue;

      if (printerType === PrinterType.Etiqueta) {
        if (!menuStore.itemMap?.[item.itemId]?.labelTemplate) continue;
      }

      if (existingCount === 0) {
        orderItemsToPrint.push(item);
      } else {
        orderItemsToPrint.push({
          ...item,
          amount: item.amount - existingCount,
        });
      }
    }

    return orderItemsToPrint;
  }

  /** True when every item's printed counter for the type is already at max. */
  function _isFullyPrinted(order: IOrder, printerType: PrinterType): boolean {
    const printKey = _getPrintKey(printerType);
    const totalAmount = order.orderItems.reduce(
      (acc, item) => acc + item.amount,
      0
    );
    const printedAmount = order.orderItems.reduce(
      (acc, item) => acc + item[printKey],
      0
    );
    return printedAmount >= totalAmount;
  }

  function _getPrinter(printerType: PrinterType, stationId?: number) {
    switch (printerType) {
      case PrinterType.Ticket:
        return store.ticketPrinter;
      case PrinterType.Comanda:
        return typeof stationId === 'number'
          ? store.comandaPrintersByStation[stationId] ?? null
          : null;
      case PrinterType.Etiqueta:
        return store.stickerPrinter;
    }
  }

  function _hasAnyComandaPrinter() {
    return Object.values(store.comandaPrintersByStation).some((p) => !!p);
  }

  /** @returns the number of labels actually printed. Throws on print failure. */
  async function _handleStickerPrinting({
    printer,
    order,
    items,
    printContext,
  }: {
    printer: IPrinter;
    order: IOrder;
    items: IOrderItem[];
    printContext?: IPrintContext;
  }): Promise<number> {
    let printedLabels = 0;
    const printedByIndex = new Map<number, number>();

    // $INDICE_ITEM context: order-wide, from the FULL templated non-canceled
    // lines (not the possibly-delta `items`). String(index) is unique per
    // order — a collision would silently corrupt the offset math.
    const templatedLines = (order.orderItems ?? []).filter(
      (oi) => !oi.canceledAt && !!menuStore.itemMap?.[oi.itemId]?.labelTemplate
    );

    // Reprint detection is data-driven: a delta run always passes lines with
    // amount reduced by printE, so a full-amount line that already has
    // printed counters can only come from a reprint ('all' / re-print of a
    // fully printed order). First prints (printE=0 everywhere) are
    // offset-neutral either way.
    const isReprint = items.some((it) => {
      const original = order.orderItems?.find((oi) => oi.index === it.index);
      if (!original) return false;
      return (original.printE ?? 0) > 0 && it.amount >= original.amount;
    });

    const stickerIndexContext = computeStickerIndexContext({
      lines: templatedLines.map((oi) => ({
        lineId: String(oi.index),
        index: oi.index,
        amount: oi.amount,
        printE: oi.printE ?? 0,
      })),
      isReprint,
    });

    // Per-printer ZPL settings, resolved once for the whole batch — but only
    // EXPLICIT settings are forwarded: without them LabelPrinter resolves the
    // legacy localStorage `stickerSettings` itself and emits pre-parity bytes
    // (no offset triplet), keeping unconfigured clients byte-identical.
    const { zpl, zplExplicit } = store.printerSettingsOf(printer);
    const explicitZpl = zplExplicit ? zpl : undefined;

    // DELIBERATELY SERIAL (WP2b): every label targets the same printer (the
    // strategy chain would serialize them anyway) and label emission order
    // is user-meaningful ($INDICE_ITEM x/total sequence off the printer).
    for (const item of items) {
      const lineStartIndex = resolveStickerLineStartIndex(
        stickerIndexContext,
        String(item.index)
      );

      for (let i = 0; i < item.amount; i++) {
        // Per-label dedupeKey discriminator: each physical label is its own
        // job — a shared key would make the bridge's 90s dedupe window
        // swallow every label after the first.
        const labelContext: IPrintContext | undefined = printContext
          ? {
              ...printContext,
              dedupeKey: printContext.dedupeKey
                ? `${printContext.dedupeKey}:${item.index}.${i + 1}`
                : undefined,
            }
          : undefined;

        const menuItem = menuStore.itemMap?.[item.itemId];
        try {
          await LabelPrinter.printItemLabel({
            item: menuItem ?? item.item,
            template: menuItem?.labelTemplate ?? item.item.labelTemplate,
            order,
            orderItem: item,
            manualVariables: [],
            zpl: explicitZpl,
            printFn: async (zplDoc) =>
              await printZpl(printer, zplDoc, labelContext, order.id),
            itemMap: menuStore.itemMap,
            itemIndex: lineStartIndex + i,
          });
        } catch (e) {
          throw new StickerBatchError(e, printedByIndex, item.index);
        }
        printedLabels++;
        printedByIndex.set(
          item.index,
          (printedByIndex.get(item.index) ?? 0) + 1
        );
      }
    }

    return printedLabels;
  }

  async function _handleTicketOrComandaPrinting({
    printer,
    order,
    items,
    printerType,
    stationId,
    printContext,
    variant,
    unitMarker,
    omitDelayedBanner,
  }: {
    printer: IPrinter;
    order: IOrder;
    items: IOrderItem[];
    printerType: PrinterType;
    stationId?: number;
    printContext?: IPrintContext;
    /**
     * 'summary' = pre-accept ticket: banner, no payment info, no footer.
     * 'reprint' = confirmed full reprint: REIMPRESIÓN banner (comanda only).
     */
    variant?: 'summary' | 'reprint';
    /** Unit-mode comanda documents: printed after the item name as ` (n/total)`. */
    unitMarker?: string;
    /** printGroup documents never carry the DEMORADA banner. */
    omitDelayedBanner?: boolean;
  }) {
    const { currency, restaurant, selectedBranch: branch } = restaurantStore;

    const { categories } = menuStore;

    const ticketSettings = vueData.ticketSettings;

    const printingSettings =
      printerType === PrinterType.Ticket
        ? store.ticketPrintingSettings
        : store.comandaPrintingSettings;

    // Per-printer hardware settings (codepage falls back to the
    // destination's legacy encoding, then cp858).
    const { escpos } = store.printerSettingsOf(
      printer,
      printingSettings.encoding
    );

    const comanda = printerType === PrinterType.Comanda;

    const logoBytes =
      !comanda && ticketSettings.showLogo && ticketSettings.logoPath
        ? await getLogoBytes(ticketSettings.logoPath, escpos)
        : null;

    const encoder = encoderForSettings(escpos);

    encoder.codepage(escpos.codepage);
    encoder.align('left');
    encoder.newline();

    // Print Header
    await _printHeader(encoder, {
      comanda,
      logoBytes,
      ticketSettings,
      restaurant,
      branch,
      stationId,
    });

    if (variant === 'summary' && !comanda) {
      encoder.align('center');
      encoder.boldLine('RESUMEN DE PEDIDO');
      encoder.line('Pedido sin confirmar');
      encoder.align('left');
      encoder.separator();
    }

    // Confirmed full reprint: physical marker, comanda documents only.
    if (variant === 'reprint' && comanda) {
      encoder.align('center');
      encoder.boldLine('*** REIMPRESIÓN ***');
      encoder.align('left');
      encoder.separator();
    }

    // Late comanda (recovery backlog after a printer outage): the kitchen
    // must see at a glance that the ticket is old. Each item is anchored on
    // the newest of modifiedAt / addedAt, falling back to order.createdAt
    // (see delayedPrintMinutesOf). The manual 'reprint' variant is excluded:
    // a deliberate reprint of an old table already prints REIMPRESIÓN and
    // must not add DEMORADA noise.
    if (comanda && variant !== 'reprint' && !omitDelayedBanner) {
      const delayedMinutes = delayedPrintMinutesOf({
        items,
        orderCreatedAt: order.createdAt,
        now: Date.now(),
      });
      if (delayedMinutes !== null) {
        encoder.align('center');
        encoder.boldLine(`*** DEMORADA ${delayedMinutes} MIN ***`);
        encoder.align('left');
        encoder.separator();
      }
    }

    // Print Order Info
    _printOrderInfo(encoder, { order, comanda });

    // Print Order Fields
    _printOrderFields(encoder, order);

    encoder.separator();

    // Print Items
    const optionsByIndex = getDisplayOptions(order.orderItems);

    const addedItems = _printItems(encoder, {
      items,
      optionsByIndex,
      categories,
      currency,
      comanda,
      unitMarker,
    });

    if (!addedItems) {
      console.log('Ignoring print, no items to print');
      return;
    }

    // Print Order Comments
    _printOrderComments(encoder, order);

    // Print Totals
    if (!comanda) {
      _printTotals(encoder, { order, currency });

      // Club loyalty lines (post-payment only: earnedPoints is 0 until Paid).
      for (const clubLine of computeClubTicketLines([order])) {
        encoder.keyValue(`${clubLine.label}:`, clubLine.valueDisplay);
      }
    }

    // Print Payment Info (a pre-accept summary has no payment yet)
    if (!comanda && variant !== 'summary') {
      _printPaymentInfo(encoder, { order, currency });
    }

    // Print Away Order Info
    _printAwayOrderInfo(encoder, { order, comanda });

    // Print Footer
    if (!comanda && variant !== 'summary') {
      await _printFooter(encoder, {
        order,
        ticketSettings,
        restaurant,
        escpos,
      });
    }

    encoder.newline();
    encoder.align('left');
    // null feedLines = this document's historical feed (byte parity).
    encoder.newline(escpos.feedLines ?? 3);

    encoder.cut(escpos.cutType, escpos.cutMode);

    const printData = encoder.encode();

    return await printByteArray(printer, printData, printContext);
  }

  /** One recent-jobs record per printed comanda document ("Reimprimir" replays exactly one document). */
  function _addComandaPrintedJob({
    printer,
    stationId,
    bytes,
    title,
    isAutoPrint,
    orderId,
  }: {
    printer: IPrinter;
    stationId: number;
    bytes: number[];
    title: string;
    isAutoPrint: boolean;
    orderId: number;
  }) {
    store.addPrintJob({
      printerType: PrinterType.Comanda,
      comandaStationId: stationId,
      printerName: printer.name,
      createdAt: new Date(),
      status: 'printed',
      data: bytes,
      isAutoPrint,
      title,
      metadata: { orderId },
    });
  }

  /**
   * Prints one station's comanda batch: the grouped document (lines that
   * print together — byte-identical to the pre-feature document, bare dedupe
   * key) followed, SERIALLY on the same printer, by one document per unit
   * (unit mode, `1x Name (n/total)`, key suffixed per unit) or per line (line
   * mode, `Nx Name`, key suffixed `.L`) of each split line. Serial on
   * purpose: the documents share a physical printer, the kitchen tears them
   * off in sequence, only one job is ever queued per station, and a failure
   * aborts at a known point so the progress carried by ComandaBatchError is
   * exact.
   *
   * @returns whether any document produced output.
   * @throws ComandaBatchError with the units confirmed printed so far and the
   * units of the document that was in flight.
   */
  async function _printComandaStationDocs({
    printer,
    order,
    items,
    stationId,
    printContext,
    variant,
    titleBase,
    omitDelayedBanner,
  }: {
    printer: IPrinter;
    order: IOrder;
    items: IOrderItem[];
    stationId: number;
    printContext: IPrintContext;
    variant?: 'summary' | 'reprint';
    /** Jobs-tab title of the grouped document; unit documents append ` · <name> n/total`, line documents ` · Nx <name>`. */
    titleBase: string;
    /** printGroup only: its documents never carry DEMORADA. */
    omitDelayedBanner?: boolean;
  }): Promise<boolean> {
    const plan = planComandaStationDocs({
      lines: items,
      splitOf: _comandaSplitOf,
      originalAmountOf: (line) =>
        order.orderItems.find((oi) => oi.index === line.index)?.amount ??
        line.amount,
    });

    const docs: IComandaDocJob[] = [];
    if (plan.grouped.length) {
      docs.push({
        items: plan.grouped,
        title: titleBase,
        dedupeKey: printContext.dedupeKey,
        units: new Map(
          plan.grouped.flatMap((line): [number, number][] =>
            line.index === undefined ? [] : [[line.index, line.amount]]
          )
        ),
      });
    }
    for (const doc of plan.separate) {
      const name = doc.line.item.name;
      docs.push({
        items: [doc.line],
        title: doc.marker
          ? `${titleBase} · ${name} ${doc.marker}`
          : `${titleBase} · ${doc.units}x ${name}`,
        dedupeKey: printContext.dedupeKey
          ? comandaDocDedupeKey(printContext.dedupeKey, doc)
          : undefined,
        unitMarker: doc.marker ?? undefined,
        units: new Map([[doc.index, doc.units]]),
      });
    }

    const printedUnits = new Map<number, number>();
    let produced = false;

    for (const doc of docs) {
      // NEW context object per document — never mutate the shared one.
      const docContext: IPrintContext = {
        ...printContext,
        label: doc.title,
        dedupeKey: doc.dedupeKey,
      };
      let bytes: number[] | undefined;
      try {
        bytes = await _handleTicketOrComandaPrinting({
          printer,
          order,
          items: doc.items,
          printerType: PrinterType.Comanda,
          stationId,
          printContext: docContext,
          variant,
          unitMarker: doc.unitMarker,
          omitDelayedBanner,
        });
      } catch (e) {
        throw new ComandaBatchError(
          e,
          new Map(printedUnits),
          doc.units,
          doc.title
        );
      }
      // Builder bailed (only canceled lines): no record, no credit, not a
      // failure — same as a single document today.
      if (!bytes) continue;
      produced = true;
      _addComandaPrintedJob({
        printer,
        stationId,
        bytes,
        title: doc.title,
        isAutoPrint: !!docContext.dedupeKey,
        orderId: order.id,
      });
      for (const [index, n] of doc.units) {
        printedUnits.set(index, (printedUnits.get(index) ?? 0) + n);
      }
    }

    return produced;
  }

  async function _printHeader(
    encoder: EscPosEncoder,
    {
      comanda,
      logoBytes,
      ticketSettings,
      restaurant,
      branch,
      stationId,
    }: {
      comanda: boolean;
      logoBytes: Uint8Array | null;
      ticketSettings: any;
      restaurant: IRestaurant;
      branch: IBranch;
      stationId?: number;
    }
  ) {
    if (!comanda && logoBytes && ticketSettings.showLogo) {
      encoder.align('center');

      encoder.raw(logoBytes);

      encoder.newline();
      encoder.newline();
      encoder.align('left');
    }

    if (comanda && typeof stationId === 'number') {
      encoder
        .align('center')
        .line(`COMANDA ${restaurantStore.stationName(stationId).toUpperCase()}`)
        .newline()
        .align('left');
    }

    if (!comanda) {
      if (ticketSettings.startText) {
        encoder.line(ticketSettings.startText);
      }

      encoder.keyValue('TIENDA:', restaurant.name);

      if (ticketSettings.showAddress) {
        encoder.line(branch.address);
        const city = branch.location?.name ?? branch.city,
          province = branch.location?.parent?.name ?? branch.province;
        if (city) encoder.line(city);
        if (province && city !== province) encoder.line(province);
      } else encoder.newline();
    }
  }

  function _printOrderInfo(
    encoder: EscPosEncoder,
    { order, comanda }: { order: IOrder; comanda: boolean }
  ) {
    encoder.keyValue(
      'FECHA PEDIDO:',
      new Date(order.createdAt).toLocaleString()
    );
    encoder.keyValue('FECHA IMPRESIÓN:', new Date().toLocaleString());

    const paymentMethod = Globals.shortPaymentMethods.find(
      (value) => value.id === order.paymentMethod
    );

    switch (order.type) {
      case OrderType.Away:
        encoder.keyValue('TIPO PEDIDO:', 'Take away (para retirar)');
        if (!order.manual && paymentMethod)
          encoder.keyValue('FORMA DE PAGO:', paymentMethod.title);
        encoder.newline();
        encoder.keyValue('CLIENTE:', order.userName);
        if (order.userWhatsapp || order.userTel)
          encoder.keyValue('TELÉFONO:', order.userWhatsapp || order.userTel);
        break;
      case OrderType.Delivery:
        encoder.keyValue('TIPO PEDIDO:', 'Delivery (a domicilio)');
        if (!order.manual && paymentMethod)
          encoder.keyValue('FORMA DE PAGO:', paymentMethod.title);
        encoder.newline();
        encoder.keyValue('CLIENTE:', order.userName);
        encoder.keyValue(
          'DIRECCIÓN:',
          order.userAddress +
            (order.userAddress2 ? ` ${order.userAddress2}` : '')
        );
        if (order.userAddressComments)
          encoder.keyValue('NOTAS SOBRE DIRECCIÓN:', order.userAddressComments);
        encoder.keyValue('TELÉFONO:', order.userWhatsapp || order.userTel);
        break;
      case OrderType.Seat:
        encoder.keyValue('TIPO PEDIDO:', 'A la mesa');
        if (!order.manual && paymentMethod)
          encoder.keyValue('FORMA DE PAGO:', paymentMethod.title);
        encoder.newline();
        encoder.keyValue('MESA:', order.table?.name);
        {
          // Session embed is authoritative when present (covers null =
          // cleared — must not resurrect the legacy per-order value).
          const covers = order.tableSession
            ? order.tableSession.covers
            : order.covers ?? null;
          if (covers != null && vueData.ticketSettings?.showCovers !== false)
            encoder.keyValue('CUBIERTOS:', String(covers));
        }
        if (order.userName) encoder.keyValue('CLIENTE:', order.userName);
        break;
    }
  }

  function _printOrderFields(encoder: EscPosEncoder, order: IOrder) {
    if (order.orderFields) {
      for (const f of order.orderFields) {
        encoder.keyValue(f.field.title + ':', f.value);
      }
    }
  }

  function _printItems(
    encoder: EscPosEncoder,
    {
      items,
      optionsByIndex,
      categories,
      currency,
      comanda,
      unitMarker,
    }: {
      items: IOrderItem[];
      optionsByIndex: any;
      categories: ICategory[];
      currency: ICurrency;
      comanda: boolean;
      /** Unit-mode comanda documents only: ` (n/total)` after the item name. */
      unitMarker?: string;
    }
  ): number {
    const ticketSettings = vueData.ticketSettings;
    const height = comanda
      ? ticketSettings.comandaSize
      : ticketSettings.ticketSize;
    encoder.setSize(1, height);

    encoder.boldLine(`ITEMS`);

    let addedItems = 0;

    for (const item of items) {
      if (item.canceledAt) continue;

      encoder.boldText(`${item.amount}x `);
      encoder.text(`${item.item.name}`);
      if (unitMarker) encoder.text(` (${unitMarker})`);
      encoder.newline();

      if (optionsByIndex[item.index]) {
        for (const option of optionsByIndex[item.index]) {
          if (!option.compact) {
            encoder.line('  - ' + option.option.title);
            for (const opt of option.optionItems) {
              // El italic en la impresora virtual agrega un "4" al principio y al final, verificar en impresoras reales
              encoder.italicText(
                '    - ' +
                  getOptionQuantityPrefix(opt.optionItem.option.type, opt.val)
              );
              encoder.text(`${opt.optionItem.title} `);
              if (!comanda)
                encoder.italicText(
                  `(${currency.symbol}${displayPrice(opt.total, true)})`
                );
              encoder.newline();
            }
          } else {
            encoder.text('  - ' + option.option.title + ': ');
            encoder.text(option.description);
            encoder.newline();
          }
        }
      }
      if (item.comment && item.comment.trim() !== '') {
        encoder.italicText(`  - Observación: ${item.comment}`);
        encoder.newline();
      }

      if (!comanda) {
        encoder.align('right');
        encoder.boldLine(`${currency.symbol}${displayPrice(item.total, true)}`);
        encoder.align('left');
      }

      addedItems++;
    }

    return addedItems;
  }

  function _printOrderComments(encoder: EscPosEncoder, order: IOrder) {
    if (order.comment && order.comment.trim() !== '') {
      encoder.separator();
      encoder.keyValue(`OBSERVACIÓN DEL PEDIDO:`, order.comment);
    }
  }

  function _printTotals(
    encoder: EscPosEncoder,
    { order, currency }: { order: IOrder; currency: ICurrency }
  ) {
    encoder.separator();

    const deliveryCost =
      order.type === OrderType.Delivery && order.deliveryCost > 0
        ? order.deliveryCost
        : null;

    if (deliveryCost || order.taxMode === 3) {
      encoder.keyValue(
        'SUBTOTAL:',
        `${currency.symbol}${displayPrice(
          order.total - (order.deliveryCost || 0) - (order.taxAmount || 0),
          true
        )}`
      );

      if (deliveryCost)
        encoder.keyValue(
          'COSTO DE ENVÍO:',
          `${currency.symbol}${displayPrice(order.deliveryCost, true)}`
        );

      if (order.taxMode === 3)
        encoder.keyValue(
          `${order.taxName} (%${order.tax})`,
          `${currency.symbol}${displayPrice(order.taxAmount, true)}`
        );

      encoder.separator();
    }

    const discountResult = computeOrderDiscountLines(order, currency.symbol);

    for (const line of discountResult.lines) {
      encoder.keyValue(`${line.label}:`, line.valueDisplay);
    }

    // If multiple discounts, show total
    if (discountResult.lines.length > 1) {
      encoder.separator();
      encoder.keyValue(
        'DESCUENTO TOTAL:',
        `(${currency.symbol}${displayPrice(
          discountResult.totalDiscountAmount,
          true
        )})`
      );
    }

    encoder.keyValue(
      'TOTAL:',
      `${currency.symbol}${displayPrice(order.total, true)}`
    );

    // Add tip if present
    if (order.tipTotal && order.tipTotal > 0) {
      encoder.separator();
      encoder.keyValue(
        'PROPINA:',
        `${currency.symbol}${displayPrice(order.tipTotal, true)}`
      );
      encoder.keyValue(
        'TOTAL CON PROPINA:',
        `${currency.symbol}${displayPrice(order.total + order.tipTotal, true)}`
      );
    }

    // Add transparency fiscal section for Argentina
    if (restaurantStore.restaurant?.country === 'AR') {
      _printTransparencyFiscal(encoder, { orders: [order], currency });
    }
  }

  /**
   * Print transparency fiscal section for Argentinian restaurants
   * Works for both single orders and groups of orders
   */
  function _printTransparencyFiscal(
    encoder: EscPosEncoder,
    {
      orders,
      currency,
      vatOverride,
      otherTaxesOverride,
    }: {
      orders?: IOrder[];
      currency: ICurrency;
      vatOverride?: number;
      otherTaxesOverride?: number;
    }
  ) {
    // Use override values if provided, otherwise calculate from orders
    let vatAmount = typeof vatOverride === 'number' ? vatOverride : 0;
    let otherTaxesAmount =
      typeof otherTaxesOverride === 'number' ? otherTaxesOverride : 0;
    let hasVatItems = false;

    // Calculate VAT from orders if no override was provided
    if (orders && typeof vatOverride !== 'number') {
      // Check if there are items with vat property and calculate VAT
      for (const order of orders) {
        const orderItems = order.orderItems || [];
        for (const item of orderItems) {
          if (item.canceledAt) continue;

          if (typeof item.item?.vat === 'number' && item.item.vat > 0) {
            hasVatItems = true;
            // Calculate VAT amount: price * amount * vat / (100 + vat)
            // This formula extracts the VAT from the total price
            const vatRate = item.item.vat / 100;
            const itemVatAmount = (item.total * vatRate) / (1 + vatRate);
            vatAmount += itemVatAmount;
          }
        }
      }

      // Round vat amount to 2 decimals
      vatAmount = Math.round(vatAmount * 100) / 100;
    } else if (typeof vatOverride === 'number') {
      // If override was provided, consider we have VAT items
      hasVatItems = vatOverride > 0;
    }

    if (hasVatItems || vatAmount > 0) {
      // Print transparency fiscal section
      encoder.separator();
      encoder.boldLine(
        'RÉGIMEN DE TRANSPARENCIA FISCAL AL CONSUMIDOR LEY 27.743'
      );

      encoder.keyValue(
        'IVA:',
        `${currency.symbol}${displayPrice(vatAmount, true)}`
      );

      if (otherTaxesAmount > 0) {
        encoder.keyValue(
          'OTROS IMPUESTOS NACIONALES INDIRECTOS:',
          `${currency.symbol}${displayPrice(otherTaxesAmount, true)}`
        );
      }
    }
  }

  function _printPaymentInfo(
    encoder: EscPosEncoder,
    { order, currency }: { order: IOrder; currency: ICurrency }
  ) {
    encoder.setSize(1, 1);
    encoder.separator();

    if (!order.totalPaid) {
      encoder.keyValue('PAGADO:', `${currency.symbol}0`);
    } else {
      if (order.paymentsByType) {
        const paymentsByType = parseOrderPaymentsByType(order);
        for (const key in paymentsByType) {
          const paidAmount = paymentsByType[key];

          if (paidAmount > 0) {
            const paymentTypeTitle = (
              paymentTypesTitles[key] ?? key
            ).toUpperCase();
            encoder.keyValue(
              `${paymentTypeTitle}:`,
              `${currency.symbol}${displayPrice(paymentsByType[key], true)}`
            );
          }
        }
      } else {
        encoder.keyValue(
          'PAGADO:',
          `${currency.symbol}${displayPrice(order.totalPaid, true)}`
        );
      }
      encoder.keyValue(
        'FALTA PAGAR:',
        `${currency.symbol}${displayPrice(order.total - order.totalPaid, true)}`
      );
    }
  }

  function _printAwayOrderInfo(
    encoder: EscPosEncoder,
    { order, comanda }: { order: IOrder; comanda: boolean }
  ) {
    if (order.type === OrderType.Away) {
      if (comanda) encoder.separator();

      encoder.newline();
      encoder.align('center');
      encoder.boldLine(`* Retirar con el número ${order.humanId} *`);
      encoder.align('left');
    }
  }

  async function _printFooter(
    encoder: EscPosEncoder,
    {
      order,
      ticketSettings,
      restaurant,
      escpos,
    }: {
      order: IOrder;
      ticketSettings: any;
      restaurant: IRestaurant;
      escpos: IEscposPrinterSettings;
    }
  ) {
    encoder.align('center');

    if (ticketSettings.endText) {
      encoder.newline();
      encoder.text(ticketSettings.endText);
    }

    if (ticketSettings.showQR) {
      await renderQr(encoder, `https://${domain}/${restaurant.stringId}`, {
        qrMode: escpos.qrMode,
        paperWidthMm: escpos.paperWidthMm,
      });
      encoder.newline();
      encoder.line(`Escanee el QR o ingrese en`);
    } else {
      encoder.newline();
      encoder.line(`Ingrese en`);
    }
    encoder.line(
      `${domain}/${restaurant.stringId} para ver la carta en su teléfono`
    );
    encoder.align('left');
  }
  //#endregion

  /**
   * Effective comanda routing of an item: item-level override wins, else the
   * category's. Routing reads the LIVE catalog item (menu store, reloaded on
   * catalogUpdate) instead of the order's embedded snapshot, so printing-tab
   * edits apply to reprints of already-loaded orders without a page reload;
   * the snapshot is only a fallback (catalog not loaded / item deleted).
   * When the routing can't be resolved (e.g. deleted category), either
   * throws (default, keeps items retryable) or falls back to station 1.
   */
  function _getEffectiveComandaPrinters(
    item: IOrderItem,
    fallbackToDefault = false
  ): number[] {
    const menuItem = menuStore.itemMap?.[item.itemId] ?? item.item;
    // Null while the catalog (re)loads, e.g. right after a store switch.
    const category = menuStore.categories?.find(
      (c) => c.id === menuItem.categoryId
    );
    const itemComandaPrinters =
      menuItem.comandaPrinters ?? category?.comandaPrinters;

    if (!itemComandaPrinters) {
      if (fallbackToDefault) {
        console.error(
          'Comanda routing missing for item, defaulting to station 1',
          item.item.id
        );
        return [1];
      }

      throw new Error(
        'Error comanda, por favor contacte al soporte técnico (1)'
      );
    }

    return itemComandaPrinters;
  }

  /** Items routed to a station. */
  function _filterComandaItems(
    items: IOrderItem[],
    stationId: number,
    fallbackToDefault = false
  ) {
    return items.filter((item) =>
      _getEffectiveComandaPrinters(item, fallbackToDefault).includes(stationId)
    );
  }

  /**
   * The line's comanda layout (catalog item `comandaSplit`): together with the
   * order, one document per unit, or one per line. Same live-catalog-first
   * resolution as routing, but null-safe: a missing catalog row or an unknown
   * value must never throw here (routing already threw if unresolvable) — it
   * prints together with the order.
   */
  function _comandaSplitOf(line: IOrderItem): ComandaSplit {
    const split = (menuStore.itemMap?.[line.itemId] ?? line.item)
      ?.comandaSplit;
    return isComandaSplit(split) ? split : ComandaSplit.None;
  }

  /**
   * Manual-print reprint gate for one gated document kind. Routing-aware:
   * comanda eligibility is the union of every station's routed items (with
   * the tolerant station-1 fallback the fan-out and group print use), so
   * comanda-excluded (`comandaPrinters: []`) or permanently-unroutable items
   * can neither trigger a dead-end dialog nor lock the state to `unprinted`.
   */
  function getManualPrintGate(
    order: IOrder,
    printerType: PrinterType.Comanda | PrinterType.Etiqueta
  ): IManualPrintGate {
    // Apply locally-cached printed counters before reading them.
    usePrintedStore().mergeIntoOrder(order);

    const nonCanceledItems = (order.orderItems ?? []).filter(
      (item) => !item.canceledAt
    );

    let eligibleItems: IOrderItem[];

    if (printerType === PrinterType.Comanda) {
      // _filterComandaItems returns the same item references, so identity
      // dedupes items routed to several stations.
      const union = new Set<IOrderItem>();
      for (const station of restaurantStore.printStations) {
        for (const item of _filterComandaItems(
          nonCanceledItems,
          station.stationId,
          true
        )) {
          union.add(item);
        }
      }
      eligibleItems = Array.from(union);
    } else {
      eligibleItems = nonCanceledItems.filter(
        (item) => !!menuStore.itemMap?.[item.itemId]?.labelTemplate
      );
    }

    const printKey: 'printC' | 'printE' =
      printerType === PrinterType.Comanda ? 'printC' : 'printE';

    return computeManualPrintGateState({ eligibleItems, printKey });
  }

  // Refactor printOrder
  /**
   * @returns whether any print output (ticket/comanda bytes or labels) was
   * actually produced. Items are only marked as printed when this is true.
   */
  async function printOrder({
    printerType,
    order,
    stationId,
    printMode = 'unprintedOrAll',
    itemsToPrint,
    mustMarkPrinted = true,
    isAutoPrint = false,
    variant,
  }: {
    printerType: PrinterType;
    order: IOrder;
    stationId?: number;
    printMode?: 'all' | 'unprinted' | 'unprintedOrAll';
    itemsToPrint?: IOrderItem[];
    mustMarkPrinted?: boolean;
    /** Only the auto-print evaluator passes true: enables the cross-tab
     * dedupeKey on the submitted bridge jobs. */
    isAutoPrint?: boolean;
    /**
     * 'summary' = pre-accept document; NEVER consumes printed counters.
     * 'reprint' = confirmed full reprint; comanda docs get a REIMPRESIÓN
     * banner (counters marked normally — they are already at max).
     */
    variant?: 'summary' | 'reprint';
  }): Promise<boolean> {
    ensureOrderPrintable(order);

    // A summary must never mark items printed (it would silently suppress
    // the real ticket auto-print on accept), regardless of the caller.
    if (variant === 'summary') mustMarkPrinted = false;

    if (!itemsToPrint) {
      itemsToPrint = _getFilteredItems({
        printerType,
        order,
        printMode,
      });

      // 'unprintedOrAll' falling back to ALL items means every item was
      // already printed — that is by definition a reprint (POS v1 comanda
      // button, "Todo" on printed orders). Branding it 'reprint' prints the
      // REIMPRESIÓN banner instead of a false DEMORADA one on old orders.
      if (
        !variant &&
        printerType === PrinterType.Comanda &&
        printMode === 'unprintedOrAll' &&
        _isFullyPrinted(order, printerType)
      ) {
        variant = 'reprint';
      }
    }

    // Comanda fan-out: one print per station that has matching items.
    if (
      printerType === PrinterType.Comanda &&
      typeof stationId === 'undefined'
    ) {
      let anyStationProduced = false;

      // Guard against a stale/fallback station list (e.g. failed load): items
      // routed only to unknown stations are never part of any station's batch
      // below, so they naturally stay unmarked and retryable.
      const knownStationIds = new Set(
        restaurantStore.printStations.map((s) => s.stationId)
      );
      const hasUnroutableItems = itemsToPrint.some((item) => {
        const printers = _getEffectiveComandaPrinters(item);
        return (
          printers.length > 0 && !printers.some((id) => knownStationIds.has(id))
        );
      });
      if (hasUnroutableItems) {
        vueData.addAlert(
          'No se pudieron imprimir algunas comandas, reintente en unos segundos',
          AlertType.Error
        );
      }

      // Cross-station aggregation: an item shared across stations is marked
      // only when EVERY routing station printed or failed unsafe/unknown
      // (may have printed). A retry-safe failure at any of its stations
      // leaves it unmarked so catch-up retries it, accepting a bounded
      // duplicate at the stations that already printed — deliberate: a
      // silent loss is worse than a visible duplicate.
      //
      // WP2b: stations dispatch CONCURRENTLY (a slow station's printer must
      // not delay another station's comanda). The closures NEVER reject —
      // every path returns a record — and nothing is pushed into shared
      // arrays from inside them: the records are folded POSITIONALLY after
      // the join, so `stationOutcomes` keeps deterministic station order
      // regardless of completion order. Two stations sharing one physical
      // printer stay safe: dedupeKeys differ by stationId and the strategy
      // chain/FIFO serializes the device.
      // Narrowed capture: `itemsToPrint` is guaranteed assigned above, but
      // TS can't carry that into the closures.
      const itemsForStations = itemsToPrint;
      const stationRuns = restaurantStore.printStations.map((station) => {
        const stationItems = _filterComandaItems(
          itemsForStations,
          station.stationId
        );
        // Stations with no routed items: no record at all.
        if (!stationItems.length) return null;

        // Credited units per routed index (undefined indexes are dropped, as
        // the pre-feature index list did); 0 = certainly not printed.
        const unitsOf = (
          valueOf: (item: IOrderItem) => number
        ): ReadonlyMap<number, number> =>
          new Map(
            stationItems.flatMap((oI): [number, number][] =>
              oI.index === undefined ? [] : [[oI.index, valueOf(oI)]]
            )
          );
        const printer = _getPrinter(printerType, station.stationId);
        if (!printer) {
          // Synchronous prefix: same alert count and ordering as the serial
          // version (all missing-printer alerts fire before any print I/O).
          vueData.addAlert(
            `No hay impresora de comanda configurada para ${station.name}`,
            AlertType.Error
          );
          return Promise.resolve({
            outcome: { creditedUnits: unitsOf(() => 0) },
            produced: false,
            retryableFailure: true,
          });
        }

        return (async () => {
          try {
            const stationProduced = await printOrder({
              printerType,
              order,
              stationId: station.stationId,
              printMode,
              itemsToPrint: stationItems,
              mustMarkPrinted: false,
              isAutoPrint,
              variant,
            });
            // A station can resolve without producing output (nothing
            // printable): its items must not be marked, but nothing was lost
            // either, so it is not a retryable failure. `oI.amount` is the
            // passed (delta) amount.
            return {
              outcome: {
                creditedUnits: unitsOf((oI) =>
                  stationProduced ? oI.amount : 0
                ),
              },
              produced: stationProduced,
              retryableFailure: false,
            };
          } catch (e) {
            console.error(`Error imprimiendo comanda ${station.name}`, e);
            if (e instanceof ComandaBatchError) {
              // Split batch: credit exactly the documents that came out
              // (plus the in-flight one on an unsafe/unknown cause). A
              // never-attempted or safe-failed remainder is retryable; a
              // batch whose LAST document failed unsafe has none.
              const credited = creditedComandaUnits(
                e,
                hasUnsafeRetrySafety(e.cause)
              );
              return {
                outcome: {
                  creditedUnits: unitsOf((oI) => credited.get(oI.index) ?? 0),
                },
                produced: e.printedUnits.size > 0,
                retryableFailure: stationItems.some(
                  (oI) =>
                    oI.index !== undefined &&
                    !oI.canceledAt &&
                    (credited.get(oI.index) ?? 0) < oI.amount
                ),
              };
            }
            // 'unsafe'/'unknown' → the comanda MAY have printed: mark anyway
            // so catch-up never silently re-prints; the Trabajos tab is the
            // human check. 'safe' / plain Error → leave unmarked for
            // catch-up.
            const mayHavePrinted = hasUnsafeRetrySafety(e);
            return {
              outcome: {
                creditedUnits: unitsOf((oI) =>
                  mayHavePrinted ? oI.amount : 0
                ),
              },
              produced: false,
              retryableFailure: !mayHavePrinted,
            };
          }
        })();
      });

      const stationRecords = await Promise.all(stationRuns);

      const stationOutcomes: IComandaStationOutcome[] = [];
      let hadRetryableStationFailure = false;
      for (const record of stationRecords) {
        if (!record) continue;
        stationOutcomes.push(record.outcome);
        anyStationProduced = anyStationProduced || record.produced;
        hadRetryableStationFailure =
          hadRetryableStationFailure || record.retryableFailure;
      }

      if (mustMarkPrinted) {
        // Unit-granular: an index is credited the MINIMUM across its routing
        // stations; for orders without split lines this yields exactly the
        // pre-feature marks (printC + delta = amount).
        _markCreditedUnits({
          order,
          printerType,
          unitsByIndex: computeMarkableComandaUnits(stationOutcomes),
        });
      }

      // Retryable failures (caught errors credited short or nothing, missing
      // printers, unroutable items) left items unmarked: freeze the catch-up
      // sync point. Benign no-output stations don't count — nothing was lost
      // and they could recur forever, permanently preventing the thaw.
      if (isAutoPrint && (hadRetryableStationFailure || hasUnroutableItems)) {
        _noteAutoPrintRetryableFailure();
      }

      return anyStationProduced;
    }

    const printer = _getPrinter(printerType, stationId);

    if (!printer) {
      vueData.addAlert(
        `No hay impresora de ${PrinterType[printerType]} configurada`,
        AlertType.Error
      );
      if (isAutoPrint) _noteAutoPrintRetryableFailure();
      return false;
    }

    const pedidoTitle = order.table
      ? `mesa ${order.table.name}`
      : `#${order.id}`;

    /** Bridge meta.label AND Jobs-tab title of this order's document(s). */
    const docLabel = `${
      printerType === PrinterType.Ticket
        ? 'Ticket'
        : printerType === PrinterType.Comanda
        ? 'Comanda'
        : 'Etiqueta'
    } pedido ${pedidoTitle}`;

    const printContext: IPrintContext = {
      kind:
        printerType === PrinterType.Ticket
          ? 'ticket'
          : printerType === PrinterType.Comanda
          ? 'comanda'
          : 'sticker',
      label: docLabel,
      stationId: printerType === PrinterType.Comanda ? stationId : undefined,
    };

    if (isAutoPrint) {
      // Deterministic across tabs: two open admin tabs auto-printing the same
      // order collapse into one physical print via the bridge's 90s window.
      printContext.dedupeKey =
        'auto:' +
        order.id +
        ':' +
        printerType +
        ':' +
        (stationId ?? 0) +
        ':' +
        itemsToPrint.map((i) => i.index + '.' + i.amount).join('|');
    }

    let printedBytes: number[];
    let producedOutput = false;

    try {
      switch (printerType) {
        case PrinterType.Etiqueta:
          producedOutput =
            (await _handleStickerPrinting({
              printer,
              order,
              items: itemsToPrint,
              printContext,
            })) > 0;
          break;
        case PrinterType.Ticket:
          printedBytes = await _handleTicketOrComandaPrinting({
            printer,
            order,
            items: itemsToPrint,
            printerType,
            stationId,
            printContext,
            variant,
          });
          producedOutput = !!printedBytes;
          break;
        case PrinterType.Comanda:
          // One station batch: grouped document + the separate documents of
          // each split line (one per unit in unit mode, one per line in line
          // mode). It records its own job entries; `printedBytes` stays
          // unassigned so the ticket-only success record below skips.
          producedOutput = await _printComandaStationDocs({
            printer,
            order,
            items: _filterComandaItems(itemsToPrint, stationId),
            stationId,
            printContext,
            variant,
            titleBase: docLabel,
          });
          break;
      }
    } catch (e) {
      // A comanda batch error wraps the failed DOCUMENT's real error: the
      // record describes that document (status, retry safety, rendered
      // bytes) exactly as an unwrapped failure does.
      const failure = e instanceof ComandaBatchError ? e.cause : e;
      // Every final failure lands in the recent-jobs list so the human check
      // ("did it actually print?") has something to look at.
      store.addPrintJob({
        printerType,
        comandaStationId: stationId,
        printerName: printer.name,
        createdAt: new Date(),
        status: jobStatusOfError(failure),
        errorMessage:
          failure instanceof Error ? failure.message : String(failure),
        // StickerBatchError deliberately reads as its OWN (absent) safety —
        // same non-unwrapping rule as hasUnsafeRetrySafety: the batch already
        // marked printed/ambiguous labels, the remainder is retryable.
        retrySafety: retrySafetyOf(failure),
        // Rendered bytes when the send (not the render) failed — enables
        // Reintentar/Reimprimir from the Jobs tab.
        data: failedJobBytesOf(failure),
        isAutoPrint: !!printContext.dedupeKey,
        title:
          e instanceof ComandaBatchError
            ? e.failedDocTitle
            : `${
                printerType === PrinterType.Etiqueta
                  ? 'Etiquetas'
                  : printerType === PrinterType.Ticket
                  ? 'Ticket'
                  : 'Comanda'
              } pedido ${pedidoTitle}`,
        metadata: { orderId: order.id },
      });

      // Comanda batches are one job PER DOCUMENT: mark exactly the units that
      // came out (plus the in-flight document on an unsafe/unknown cause).
      // The generic "unsafe → mark everything" branch below must never see a
      // batch error, or it would mark never-attempted units.
      if (e instanceof ComandaBatchError) {
        if (mustMarkPrinted) {
          _markCreditedUnits({
            order,
            printerType,
            unitsByIndex: creditedComandaUnits(
              e,
              hasUnsafeRetrySafety(e.cause)
            ),
          });
        }
        throw e;
      }

      // Sticker batches are one job PER LABEL: mark exactly what came out of
      // the printer (plus the ambiguous in-flight label on an unsafe/unknown
      // failure) so catch-up reprints only the true remainder.
      if (e instanceof StickerBatchError) {
        if (mustMarkPrinted) {
          const ambiguousIndex = hasUnsafeRetrySafety(e.cause)
            ? e.failedIndex
            : null;
          const indexes: number[] = [];
          const amounts: number[] = [];
          for (const item of itemsToPrint) {
            const printedNow =
              (e.printedByIndex.get(item.index) ?? 0) +
              (item.index === ambiguousIndex ? 1 : 0);
            if (!printedNow) continue;
            const original = order.orderItems.find(
              (oi) => oi.index === item.index
            );
            if (!original) continue;
            indexes.push(item.index);
            // Absolute counter: labels printed before this run + this run's.
            amounts.push(
              Math.min(original.amount, (original.printE ?? 0) + printedNow)
            );
          }
          if (indexes.length) {
            markPrinted({ order, indexes, amounts, printerType });
          }
        }
        throw e;
      }

      // Marking policy on print failure (B2/P0.5): 'unsafe'/'unknown' → the
      // document MAY have printed, mark items anyway (catch-up must never
      // silently re-print a possibly-printed document; the Trabajos tab +
      // toast is the human check). 'safe' or plain Error → leave unmarked so
      // Part 0 catch-up retries.
      if (mustMarkPrinted && hasUnsafeRetrySafety(e)) {
        markPrinted({
          order,
          indexes: itemsToPrint.map((oI) => oI.index),
          amounts: itemsToPrint.map(
            (oI) => order.orderItems.find((oi) => oi.index === oI.index)?.amount
          ),
          printerType,
        });
      }
      throw e;
    }

    // Ticket only: comanda records are added per document by
    // _printComandaStationDocs (`printedBytes` stays unassigned for comanda).
    if (printedBytes) {
      store.addPrintJob({
        printerType,
        comandaStationId: stationId,
        printerName: printer.name,
        createdAt: new Date(),
        status: 'printed',
        data: printedBytes,
        isAutoPrint: !!printContext.dedupeKey,
        title: `Ticket pedido ${pedidoTitle}`,
        metadata: {
          orderId: order.id,
        },
      });
    }

    // Never mark items as printed when no output was produced (doc builder
    // bailed): a false "printed" would permanently suppress future attempts.
    if (mustMarkPrinted && producedOutput) {
      markPrinted({
        order,
        indexes: itemsToPrint.map((oI) => oI.index),
        amounts: itemsToPrint.map(
          (oI) => order.orderItems.find((oi) => oi.index === oI.index)?.amount
        ),
        printerType,
      });
    }

    return producedOutput;
  }

  /**
   * Manual pre-accept summary ticket for an incoming order: full item/total
   * detail with a "sin confirmar" banner, no payment info or QR footer, and
   * printed counters untouched so the real ticket still auto-prints on
   * accept.
   */
  async function printOrderSummary(order: IOrder): Promise<boolean> {
    return await printOrder({
      printerType: PrinterType.Ticket,
      order,
      printMode: 'all',
      mustMarkPrinted: false,
      variant: 'summary',
    });
  }

  //requestPromises: {[id: number]: Promise<void>} = {};
  async function printGroup(
    printerType: PrinterType,
    title: string,
    orders: IOrder[],
    /**
     * Ticket groups only (the auto group-payment flush passes a dedupeKey).
     * Deliberately NOT forwarded to the per-station comanda docs: they would
     * share one dedupeKey and the bridge would collapse different stations'
     * docs into one.
     */
    printContext?: IPrintContext,
    /** 'reprint' brands comanda group docs with a REIMPRESIÓN banner. */
    options?: { variant?: 'reprint' }
  ) {
    if (printerType === PrinterType.Etiqueta) {
      // DELIBERATELY SERIAL (WP2b): every iteration targets the same sticker
      // printer, so parallelizing buys zero wall-clock time (the strategy
      // chain would serialize it anyway) and would make cross-order label
      // emission order race-dependent — labels are picked off the printer in
      // sequence and the order is user-meaningful.
      for (const order of orders) {
        await printOrder({ printerType, order, printMode: 'all' });
      }
      return;
    }

    orders.forEach((o) => ensureOrderPrintable(o));

    if (printerType === PrinterType.Comanda) {
      // Per station: the group doc with that station's grouped lines, then the
      // separate docs of its split lines — one per unit (unit mode) or per
      // line (line mode) — serially, inside the closure. WP2b:
      // station docs are DISPATCHED CONCURRENTLY (each station's synchronous
      // prep — routing, guards, printer resolution + missing-printer alert —
      // stays in the loop), then allSettled + rethrow-first. This preserves
      // the throw contract (every manual caller attaches one .catch → one
      // toast) while deliberately changing one behavior: a failed station no
      // longer aborts the remaining stations — a broken printer must not
      // block another printer's documents.
      const stationDocs: Promise<void>[] = [];

      for (const station of restaurantStore.printStations) {
        const groupedByOrderId = new Map<number, IOrderItem[]>();
        const separateByOrderId = new Map<number, IOrderItem[]>();
        let totalGrouped = 0;
        let totalSeparate = 0;

        for (const order of orders) {
          // The old group print ignored routing entirely: never fail the whole
          // group over an unresolvable item, route it to station 1 instead.
          const items = _filterComandaItems(
            order.orderItems,
            station.stationId,
            true
          ).filter((item) => !item.canceledAt);
          // Split lines (unit or line mode) leave the group document and print
          // as separate documents after it (same layout rule as the
          // single-order path).
          const grouped: IOrderItem[] = [];
          const separate: IOrderItem[] = [];
          for (const item of items) {
            (_comandaSplitOf(item) === ComandaSplit.None
              ? grouped
              : separate
            ).push(item);
          }
          groupedByOrderId.set(order.id, grouped);
          separateByOrderId.set(order.id, separate);
          totalGrouped += grouped.length;
          totalSeparate += separate.length;
        }

        if (!totalGrouped && !totalSeparate) continue;

        const printer = _getPrinter(PrinterType.Comanda, station.stationId);
        if (!printer) {
          vueData.addAlert(
            `No hay impresora de comanda configurada para ${station.name}`,
            AlertType.Error
          );
          continue;
        }

        const stationId = station.stationId;
        const groupTitle = `Comanda ${title}`;
        stationDocs.push(
          (async () => {
            // Never call _printGroupDoc with no lines: it has no
            // empty-document bail and would print a header-only ticket.
            if (totalGrouped) {
              await _printGroupDoc({
                printerType,
                title,
                orders,
                printer,
                stationId,
                getItems: (order) => groupedByOrderId.get(order.id) ?? [],
                variant: options?.variant,
              });
            }
            // Separate documents per order, serially after the group document
            // (full lines → units numbered 1..amount, line documents `Nx`; no
            // dedupe key and no counters, group prints never had either; no
            // DEMORADA, the group document has none).
            for (const order of orders) {
              const separate = separateByOrderId.get(order.id) ?? [];
              if (!separate.length) continue;
              await _printComandaStationDocs({
                printer,
                order,
                items: separate,
                stationId,
                printContext: { kind: 'comanda', label: groupTitle, stationId },
                variant: options?.variant,
                titleBase: groupTitle,
                omitDelayedBanner: true,
              });
            }
          })()
        );
      }

      const results = await Promise.allSettled(stationDocs);
      const firstRejection = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (firstRejection) throw firstRejection.reason;
      return;
    }

    await _printGroupDoc({
      printerType,
      title,
      orders,
      printer: store.ticketPrinter,
      getItems: (order) => order.orderItems,
      printContext,
    });

    // Manual group print (printContext undefined — the auto group-payment
    // flush is the ONLY caller that passes one, and its markAllMembers
    // already stamps): stamp the printed-while-paid marker for paid members
    // so the OnPaid auto-print won't duplicate their receipt. Marker-only
    // (empty indexes) on purpose: marking printT here would regress
    // OnReceive/OnConfirm/OnFinish restaurants that print table bills
    // mid-meal. Unpaid members are untouched (bill workflow keeps its OnPaid
    // ticket). Deliberate status quo: a failed manual group print stamps
    // nothing (the manual path marks nothing today either).
    if (!printContext) {
      for (const order of orders) {
        if (
          order.paymentStatus === OrderPaymentStatus.Paid &&
          !order.printedPaidAt
        ) {
          markPrinted({
            order,
            indexes: [],
            amounts: [],
            printerType: PrinterType.Ticket,
          });
        }
      }
    }
  }

  async function _printGroupDoc({
    printerType,
    title,
    orders,
    printer,
    stationId,
    getItems,
    printContext,
    variant,
  }: {
    printerType: PrinterType;
    title: string;
    orders: IOrder[];
    printer: IPrinter | null;
    stationId?: number;
    getItems: (order: IOrder) => IOrderItem[];
    printContext?: IPrintContext;
    /** 'reprint' brands comanda docs with a REIMPRESIÓN banner. */
    variant?: 'reprint';
  }) {
    const restaurant = vueData.restaurant;
    const branch = vueData.branch;
    const comanda = printerType === PrinterType.Comanda;
    const ticketSettings = vueData.ticketSettings;

    //vueData.addAlert('Imprimiendo pedido...', AlertType.Info);
    /*if (Globals.IsProduction) {
      // @ts-ignore
      Vue.$gtag.event('print', { event_label: restaurant.id });
    }*/

    const printingSettings =
      printerType === PrinterType.Ticket
        ? store.ticketPrintingSettings
        : store.comandaPrintingSettings;

    // Per-printer hardware settings (codepage falls back to the
    // destination's legacy encoding, then cp858).
    const { escpos } = store.printerSettingsOf(
      printer,
      printingSettings.encoding
    );

    const logoBytes =
      !comanda && ticketSettings.showLogo && ticketSettings.logoPath
        ? await getLogoBytes(ticketSettings.logoPath, escpos)
        : null;

    const encoder = encoderForSettings(escpos);

    encoder.codepage(escpos.codepage);
    encoder.align('left');
    encoder.newline();

    if (!comanda && logoBytes && ticketSettings.showLogo) {
      encoder.align('center');

      encoder.raw(logoBytes);

      encoder.newline();
      encoder.newline();
      encoder.align('left');
    }

    if (comanda && typeof stationId === 'number') {
      encoder
        .align('center')
        .line(`COMANDA ${restaurantStore.stationName(stationId).toUpperCase()}`)
        .newline()
        .align('left');
    }

    // Confirmed full reprint: physical marker, comanda documents only.
    // Same block as the single-order doc (separator included).
    if (comanda && variant === 'reprint') {
      encoder.align('center');
      encoder.boldLine('*** REIMPRESIÓN ***');
      encoder.align('left');
      encoder.separator();
    }

    if (!comanda) {
      if (ticketSettings.startText) {
        encoder.line(ticketSettings.startText);
      }

      encoder.keyValue('RESTAURANTE:', restaurant.name);

      if (ticketSettings.showAddress) {
        encoder.line(branch.address);
        const city = branch.location?.name ?? branch.city,
          province = branch.location?.parent?.name ?? branch.province;
        if (city) encoder.line(city);
        if (province && city !== province) encoder.line(city);
      } else encoder.newline();
    }

    encoder.keyValue(
      'FECHA PEDIDO:',
      new Date(orders[orders.length - 1].createdAt).toLocaleString()
    );
    encoder.keyValue('FECHA IMPRESIÓN:', new Date().toLocaleString());

    encoder.boldLine(title);

    // Covers of the table's session (Seat group docs; MD-5 emission order).
    const groupSession = deriveOpenTableSession(orders);
    if (groupSession?.covers != null && ticketSettings.showCovers !== false) {
      encoder.keyValue('CUBIERTOS:', String(groupSession.covers));
    }

    encoder.separator();

    const height = comanda
      ? ticketSettings.comandaSize
      : ticketSettings.ticketSize;
    encoder.setSize(1, height);

    encoder.boldLine(`ITEMS`);

    let index = 0;
    let total = 0,
      totalTax = 0,
      taxName = null;
    for (const order of orders) {
      index++;

      const currency = Globals.getCurrency(order.currency);
      totalTax += order.taxAmount ?? 0;
      total += order.total - (order.taxAmount ?? 0);
      if (order.taxName) taxName = order.taxName;

      const orderItems = getItems(order);

      // Keep "Pedido #" numbering stable across stations, but skip orders
      // with no items for this station.
      if (comanda && !orderItems.some((item) => !item.canceledAt)) continue;

      const optionsByIndex = getDisplayOptions(order.orderItems);

      encoder.boldLine('Pedido #' + index);
      if (order.userName) encoder.keyValue('Cliente:', order.userName);
      // Custom order fields (both modes, mirroring the single-order doc)
      _printOrderFields(encoder, order);
      for (const item of orderItems) {
        if (item.canceledAt) continue;

        encoder.boldText(`${item.amount}x `);
        encoder.text(`${item.item.name}`);
        encoder.newline();

        let full = false;
        if (optionsByIndex[item.index]) {
          full = true;
          for (const option of optionsByIndex[item.index]) {
            if (!option.compact) {
              encoder.line('  - ' + option.option.title);
              for (const opt of option.optionItems) {
                encoder.italicText(
                  '    - ' +
                    getOptionQuantityPrefix(opt.optionItem.option.type, opt.val)
                );
                encoder.text(`${opt.optionItem.title} `);
                if (!comanda)
                  encoder.italicText(
                    `(${currency.symbol}${displayPrice(opt.total, true)})`
                  );
                encoder.newline();
              }
            } else {
              encoder.text('  - ' + option.option.title + ': ');
              encoder.text(option.description);
              encoder.newline();
            }
          }
        }
        if (item.comment && item.comment.trim() !== '') {
          encoder.italicText(`  - Observación: ${item.comment}`);
          encoder.newline();
          full = true;
        }

        if (!comanda) {
          encoder.align('right');
          encoder.boldLine(
            `${currency.symbol}${displayPrice(item.total, true)}`
          );
          encoder.align('left');
        }
      }

      if (order.comment && order.comment.trim() !== '') {
        encoder.separator();
        encoder.keyValue(`Observación pedido #${index}:`, order.comment);
        encoder.separator();
      }
      encoder.newline();
    }

    if (!comanda) {
      encoder.separator();

      const groupCurrencySymbol = Globals.getCurrency(orders[0].currency)
        .symbol;

      // Per-order discount lines (same helper as the single ticket)
      const discountResults = orders.map((o) =>
        computeOrderDiscountLines(o, groupCurrencySymbol)
      );
      const groupDiscountTotal = roundPrice(
        discountResults.reduce(
          (sum, r) => roundPrice(sum + r.totalDiscountAmount),
          0
        )
      );

      // Pre-discount subtotal so the printed arithmetic reads
      // SUBTOTAL − DESCUENTO + IVA = TOTAL.
      encoder.keyValue(
        'SUBTOTAL:',
        `${groupCurrencySymbol}${displayPrice(
          roundPrice(total + groupDiscountTotal),
          true
        )}`
      );

      let printedDiscountLines = 0;
      for (const result of discountResults) {
        for (const line of result.lines) {
          encoder.keyValue(`${line.label}:`, line.valueDisplay);
          printedDiscountLines++;
        }
      }

      if (printedDiscountLines > 1 && groupDiscountTotal > 0) {
        encoder.separator();
        encoder.keyValue(
          'DESCUENTO TOTAL:',
          `(${groupCurrencySymbol}${displayPrice(groupDiscountTotal, true)})`
        );
      }

      if (taxName)
        encoder.keyValue(
          `${taxName}:`,
          `${groupCurrencySymbol}${displayPrice(totalTax, true)}`
        );

      encoder.separator();

      encoder.keyValue(
        'TOTAL:',
        `${groupCurrencySymbol}${displayPrice(total + totalTax, true)}`
      );

      // Tips (propina) across the group
      const tipsTotal = roundPrice(
        orders.reduce((sum, o) => roundPrice(sum + (o.tipTotal || 0)), 0)
      );
      if (tipsTotal > 0) {
        encoder.separator();
        encoder.keyValue(
          'PROPINA:',
          `${groupCurrencySymbol}${displayPrice(tipsTotal, true)}`
        );
        encoder.keyValue(
          'TOTAL CON PROPINA:',
          `${groupCurrencySymbol}${displayPrice(
            roundPrice(total + totalTax + tipsTotal),
            true
          )}`
        );
      }

      // Club loyalty lines, aggregated across the group (post-payment only:
      // earnedPoints is 0 until each order is Paid).
      for (const clubLine of computeClubTicketLines(orders)) {
        encoder.keyValue(`${clubLine.label}:`, clubLine.valueDisplay);
      }

      // Add transparency fiscal section for Argentina
      if (restaurantStore.restaurant?.country === 'AR') {
        _printTransparencyFiscal(encoder, {
          orders,
          currency: Globals.getCurrency(orders[0].currency),
        });
      }
    }

    encoder.setSize(1, 1);

    if (!comanda) {
      encoder.separator();

      let paymentsByType: Record<string, number> = {};
      let summedTotalPaid = 0;

      for (const order of orders) {
        summedTotalPaid += order.totalPaid;
        if (order.paymentsByType) {
          const orderPaymentsByType = parseOrderPaymentsByType(order);
          for (const key in orderPaymentsByType) {
            if (!paymentsByType[key]) paymentsByType[key] = 0;
            paymentsByType[key] += orderPaymentsByType[key];
          }
        }
      }

      const summedPayments = Object.values(paymentsByType).reduce(
        (acc, val) => acc + val,
        0
      );

      if (summedTotalPaid > 0) {
        if (summedPayments > 0) {
          for (const key in paymentsByType) {
            const paidAmount = paymentsByType[key];

            if (paidAmount > 0) {
              const paymentTypeTitle = (
                paymentTypesTitles[key] ?? key
              ).toUpperCase();
              encoder.keyValue(
                `${paymentTypeTitle}:`,
                `${
                  Globals.getCurrency(orders[0].currency).symbol
                }${displayPrice(paymentsByType[key], true)}`
              );
            }
          }
        } else {
          encoder.keyValue(
            'PAGADO:',
            `${Globals.getCurrency(orders[0].currency).symbol}${displayPrice(
              summedTotalPaid,
              true
            )}`
          );
        }
        /*encoder.keyValue(
          'TOTAL PAGADO:',
          `${Globals.getCurrency(orders[0].currency).symbol}${displayPrice(
            summedTotalPaid,
            true
          )}`
        );*/
        encoder.keyValue(
          'FALTA PAGAR:',
          `${Globals.getCurrency(orders[0].currency).symbol}${displayPrice(
            total + totalTax - summedTotalPaid,
            true
          )}`
        );
      }
    }

    if (!comanda) {
      encoder.align('center');

      if (ticketSettings.endText) {
        encoder.newline();
        encoder.text(ticketSettings.endText);
      }

      if (ticketSettings.showQR) {
        await renderQr(encoder, `https://${domain}/${restaurant.stringId}`, {
          qrMode: escpos.qrMode,
          paperWidthMm: escpos.paperWidthMm,
        });
        encoder.newline();
        encoder.line(`Escanee el QR o ingrese en`);
      } else {
        encoder.newline();
        encoder.line(`Ingrese en`);
      }
      encoder.line(
        `${domain}/${restaurant.stringId} para ver la carta en su teléfono`
      );
      encoder.align('left');
    }

    encoder.newline();
    encoder.align('center');
    encoder.newline();
    encoder.align('left');

    // null feedLines = this document's historical feed (byte parity).
    encoder.newline(escpos.feedLines ?? 1);

    encoder.cut(escpos.cutType, escpos.cutMode);

    const printData = encoder.encode();

    const printedBytes = await printByteArray(printer, printData, {
      kind: comanda ? 'comanda' : 'ticket',
      label: `${
        printerType === PrinterType.Ticket ? 'Ticket' : 'Comanda'
      } ${title}`,
      ...printContext,
    });

    store.addPrintJob({
      printerType,
      comandaStationId: stationId,
      printerName: printer?.name,
      createdAt: new Date(),
      status: 'printed',
      data: printedBytes,
      isAutoPrint: !!printContext?.dedupeKey,
      title: `${
        printerType === PrinterType.Ticket ? 'Ticket' : 'Comanda'
      } ${title}`,
      metadata: {
        orderIds: orders.map((o) => o.id),
      },
    });
  }

  async function printReservationsDayList(
    ops: readonly IReservationPrintOp[],
    title: string
  ) {
    const printer = store.ticketPrinter;
    const { escpos } = store.printerSettingsOf(
      printer,
      store.ticketPrintingSettings.encoding
    );
    const encoder = encoderForSettings(escpos);
    encoder.codepage(escpos.codepage);
    encoder.align('left');
    encoder.size('small');
    for (const op of ops) {
      switch (op.kind) {
        case 'title':
          encoder.align('center');
          encoder.size('normal');
          encoder.boldLine(op.text);
          encoder.size('small');
          encoder.align('left');
          break;
        case 'bold':
          encoder.boldLine(op.text);
          break;
        case 'line':
          encoder.line(op.text);
          break;
        case 'kvSpace':
          encoder.keyValueSpaceBetween(op.left, op.right);
          break;
        case 'separator':
          encoder.separator();
          break;
        case 'newline':
          encoder.newline();
          break;
      }
    }
    encoder.newline(escpos.feedLines ?? 2);
    encoder.size('normal');
    encoder.cut(escpos.cutType, escpos.cutMode);
    const data = await printByteArray(printer, encoder.encode(), {
      kind: 'ticket',
      label: title,
    });
    store.addPrintJob({
      printerType: PrinterType.Ticket,
      printerName: printer?.name,
      createdAt: new Date(),
      status: 'printed',
      data,
      title,
    });
  }

  async function printCajaResume(cajaResume: ICajaResume) {
    const restaurant = vueData.restaurant;
    const branch = vueData.branch;

    vueData.addAlert('Imprimiendo resumen de caja...', AlertType.Info);

    const ticketSettings = vueData.ticketSettings;

    const printer = store.ticketPrinter;
    const { escpos } = store.printerSettingsOf(
      printer,
      store.ticketPrintingSettings.encoding
    );

    function formatPrice(price: number) {
      if (price !== 0 && !price) return `${vueData.currency.symbol}-`;
      return `${vueData.currency.symbol}${displayPrice(price, true)}`;
    }

    function formatCoversValue(
      covers: number,
      sessionsWithoutCovers: number
    ): string {
      if (sessionsWithoutCovers <= 0) return String(covers);
      const noun = sessionsWithoutCovers === 1 ? 'mesa' : 'mesas';
      return `${covers} (${sessionsWithoutCovers} ${noun} sin datos)`;
    }

    function formatDateShort(date?: string | Date | number) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleString('es-AR', {
        hour12: false,
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    const encoder = encoderForSettings(escpos);

    encoder.codepage(escpos.codepage);
    encoder.align('left');

    encoder.size('normal');
    encoder.align('center');
    encoder.boldLine(
      (cajaResume.type === 'partial' ? 'PARCIAL' : 'FINAL') + ' CAJA'
    );
    encoder.size('small');
    encoder.newline();
    encoder.align('left');

    function formatDate(date?: string) {
      const dateObj = date ? new Date(date) : new Date();
      return dateObj.toLocaleString('es-AR', {
        hour12: false,
      });
    }

    encoder.keyValue('Tienda:', restaurant.name);
    encoder.keyValue('Caja:', cajaResume.caja.name);
    encoder.keyValue('Fecha:', formatDate());

    encoder.separator();
    encoder.boldLine('RESUMEN DE CAJA');
    encoder.separator();

    encoder.keyValueSpaceBetween(
      'Fecha de apertura:',
      formatDate(cajaResume.movementsResume.openDate)
    );
    encoder.keyValueSpaceBetween(
      'Efectivo al abrir:',
      formatPrice(cajaResume.movementsResume.cashOnOpen)
    );
    encoder.keyValueSpaceBetween(
      'Efectivo al cerrar:',
      cajaResume.movementsResume.cashOnClose
        ? formatPrice(cajaResume.movementsResume.cashOnClose)
        : 'No cerrada'
    );
    encoder.keyValueSpaceBetween(
      'Fecha de cierre:',
      cajaResume.movementsResume.closeDate
        ? formatDate(cajaResume.movementsResume.closeDate)
        : 'No cerrada'
    );

    encoder.newline();

    encoder.keyValueSpaceBetween(
      'Retiros:',
      formatPrice(cajaResume.movementsResume.withdrawals)
    );
    encoder.keyValueSpaceBetween(
      'Depositos:',
      formatPrice(cajaResume.movementsResume.deposits)
    );

    encoder.separator();
    encoder.boldLine('TOTALES DE VENTAS');
    encoder.separator();

    encoder.keyValueSpaceBetween(
      'Total de comandas:',
      formatPrice(cajaResume.ordersTotals.uninvoicedTotal)
    );
    encoder.keyValueSpaceBetween(
      'Total de ventas:',
      formatPrice(cajaResume.ordersTotals.invoicedTotal)
    );

    encoder.newline();

    encoder.keyValueSpaceBetween(
      'Cantidad de comandas:',
      cajaResume.ordersTotals.uninvoiced
    );
    encoder.keyValueSpaceBetween(
      'Cantidad de ventas:',
      cajaResume.ordersTotals.invoiced
    );

    // Mesas & cubiertos (absent on resume snapshots stored before this field existed)
    if (cajaResume.sessionsTotals) {
      encoder.keyValueSpaceBetween(
        'Mesas atendidas:',
        String(cajaResume.sessionsTotals.sessions)
      );
      encoder.keyValueSpaceBetween(
        'Cubiertos:',
        formatCoversValue(
          cajaResume.sessionsTotals.covers,
          cajaResume.sessionsTotals.sessionsWithoutCovers
        )
      );
    }

    encoder.newline();

    encoder.line('Montos sin efecto:');
    encoder.keyValueSpaceBetween(
      'Total de invitaciones:',
      formatPrice(cajaResume.salesByPaymentMethod.invitation)
    );
    encoder.keyValueSpaceBetween(
      'Total de Consumo Empleado:',
      formatPrice(cajaResume.salesByPaymentMethod.employee)
    );
    encoder.keyValueSpaceBetween(
      'Total de Pago con saldo:',
      formatPrice(cajaResume.salesByPaymentMethod.customer)
    );
    // Mirrors the server-generated resume text (utilities/caja.ts): club
    // spends move no drawer money — the cash entered at top-up time.
    encoder.keyValueSpaceBetween(
      'Total de Pago con Saldo Club:',
      formatPrice(cajaResume.salesByPaymentMethod.club ?? 0)
    );
    const tipsTotalPrint = roundPrice(
      (cajaResume.tipsByPaymentMethod?.cash ?? 0) +
        (cajaResume.tipsByPaymentMethod?.card ?? 0) +
        (cajaResume.tipsByPaymentMethod?.online ?? 0) +
        (cajaResume.tipsByPaymentMethod?.banktransfer ?? 0) +
        (cajaResume.tipsByPaymentMethod?.qr ?? 0)
    );
    encoder.keyValueSpaceBetween(
      'Total de Propinas:',
      formatPrice(tipsTotalPrint)
    );
    // Descuentos
    encoder.keyValueSpaceBetween(
      'Total descuentos:',
      `${formatPrice(cajaResume.ordersTotals.discountedTotal ?? 0)} (${
        cajaResume.ordersTotals.discounted ?? 0
      } pedidos)`
    );

    encoder.newline();

    encoder.line('Pedidos sin efecto:');
    encoder.keyValueSpaceBetween(
      'Pedidos cancelados:',
      cajaResume.ordersTotals.canceled
    );
    encoder.keyValueSpaceBetween(
      'Total pedidos cancelados:',
      formatPrice(cajaResume.ordersTotals.canceledTotal)
    );
    encoder.keyValueSpaceBetween(
      'Pedidos anulados:',
      cajaResume.ordersTotals.rejected
    );
    encoder.keyValueSpaceBetween(
      'Total pedidos anulados:',
      formatPrice(cajaResume.ordersTotals.rejectedTotal)
    );
    encoder.keyValueSpaceBetween(
      'Items cancelados:',
      cajaResume.ordersTotals.canceledItems || 0
    );
    encoder.keyValueSpaceBetween(
      'Total items cancelados:',
      formatPrice(cajaResume.ordersTotals.canceledItemsTotal || 0)
    );

    // Ride-along: waterfall (absent on resume snapshots stored before
    // these fields existed — old snapshots simply skip the section).
    if (cajaResume.waterfall) {
      const wf = cajaResume.waterfall;
      encoder.separator();
      encoder.boldLine('VENTAS (DESGLOSE)');
      encoder.separator();
      encoder.keyValueSpaceBetween(
        'Ventas brutas:',
        formatPrice(wf.grossSales)
      );
      encoder.keyValueSpaceBetween(
        'Descuentos y promociones:',
        `-${formatPrice(wf.benefitsTotal)}`
      );
      // No local fallback: the resume carries no per-order data; the API
      // backfills nonMonetaryTotal (and net netSales) on stored Zs.
      if ((wf.nonMonetaryTotal ?? 0) !== 0) {
        encoder.keyValueSpaceBetween(
          'Invitaciones y personal:',
          `-${formatPrice(wf.nonMonetaryTotal)}`
        );
      }
      encoder.keyValueSpaceBetween('Ventas netas:', formatPrice(wf.netSales));
      if (wf.taxes !== 0) {
        encoder.keyValueSpaceBetween('Impuestos:', `+${formatPrice(wf.taxes)}`);
      }
      if (wf.rounding !== 0) {
        encoder.keyValueSpaceBetween(
          'Redondeo:',
          `${wf.rounding > 0 ? '+' : '-'}${formatPrice(Math.abs(wf.rounding))}`
        );
      }
      encoder.keyValueSpaceBetween('Total:', formatPrice(wf.dayTotal));
    }

    function printSalesByPaymentMethod(
      salesByPaymentMethod: SalesByPaymentMethod
    ) {
      // Prints the breakdown of sales by payment method using the encoder
      encoder.keyValueSpaceBetween(
        'Efectivo:',
        formatPrice(salesByPaymentMethod.cash)
      );
      encoder.keyValueSpaceBetween(
        'Tarjeta:',
        formatPrice(salesByPaymentMethod.card)
      );
      encoder.keyValueSpaceBetween(
        'Online:',
        formatPrice(salesByPaymentMethod.online)
      );
      encoder.keyValueSpaceBetween(
        'Transferencia:',
        formatPrice(salesByPaymentMethod.banktransfer ?? 0)
      );
      encoder.keyValueSpaceBetween('QR:', formatPrice(salesByPaymentMethod.qr));
      /*encoder.keyValueSpaceBetween('Invitación:', formatPrice(salesByPaymentMethod.invitation));
    encoder.keyValueSpaceBetween('Empleado:', formatPrice(salesByPaymentMethod.employee));
    encoder.keyValueSpaceBetween('Saldo cliente:', formatPrice(salesByPaymentMethod.customer));*/
      encoder.newline();
      encoder.keyValueSpaceBetween(
        'Total:',
        formatPrice(
          salesByPaymentMethod.cash +
            salesByPaymentMethod.card +
            salesByPaymentMethod.online +
            (salesByPaymentMethod.banktransfer ?? 0) +
            salesByPaymentMethod.qr
          //salesByPaymentMethod.invitation +
          //salesByPaymentMethod.employee +
          //salesByPaymentMethod.customer
        )
      );
    }

    encoder.separator();
    encoder.boldLine('VENTAS POR FORMA DE PAGO');
    encoder.separator();

    printSalesByPaymentMethod(cajaResume.salesByPaymentMethod);

    if (
      cajaResume.salesByType &&
      Object.keys(cajaResume.salesByType).length > 1
    ) {
      encoder.separator();
      encoder.boldLine('VENTAS POR TIPO');
      encoder.newline();

      const paymentTypeNames = {
        [PaymentType.Order]: 'Pedidos',
        [PaymentType.CustomerBalance]: 'Carga de saldo cliente',
      };

      for (const key in cajaResume.salesByType) {
        const title = paymentTypeNames[key as PaymentType] ?? key;

        encoder.boldLine(title);
        encoder.newline();

        printSalesByPaymentMethod(cajaResume.salesByType[key]);

        encoder.newline();
      }
    }

    if (cajaResume.tipsByPaymentMethod) {
      encoder.separator();
      encoder.boldLine('PROPINAS POR FORMA DE PAGO');
      encoder.separator();

      // Print tips by payment method
      const tips = cajaResume.tipsByPaymentMethod;
      printSalesByPaymentMethod(tips);
    }

    // Ride-along: non-sale money collected (absent on old snapshots).
    const nonSale = cajaResume.nonSaleCollections;
    if (nonSale && (nonSale.total !== 0 || nonSale.clubTopupsBonus !== 0)) {
      encoder.separator();
      encoder.boldLine('COBROS NO VENTA');
      encoder.separator();
      if (nonSale.clubTopupsPrincipal !== 0) {
        encoder.keyValueSpaceBetween(
          'Cargas de Saldo Club:',
          formatPrice(nonSale.clubTopupsPrincipal)
        );
      }
      if (nonSale.clubTopupsBonus !== 0) {
        encoder.keyValueSpaceBetween(
          'Bono Club regalado (no es dinero):',
          formatPrice(nonSale.clubTopupsBonus)
        );
      }
      if (nonSale.balanceLoads !== 0) {
        encoder.keyValueSpaceBetween(
          'Cargas de saldo cliente:',
          formatPrice(nonSale.balanceLoads)
        );
      }
      encoder.keyValueSpaceBetween(
        'Total cobros no venta:',
        formatPrice(nonSale.total)
      );
    }

    // Manual movements detail (absent on resume snapshots stored before this field existed)
    if (cajaResume.movementsResume.movements?.length) {
      encoder.separator();
      encoder.boldLine('RETIROS Y DEPOSITOS');
      encoder.separator();

      encoder.size('small');

      for (const movement of cajaResume.movementsResume.movements) {
        const description =
          movement.description ??
          (movement.type === 'inflow' ? 'Ingreso' : 'Retiro');
        const methodTitle =
          movement.paymentMethod != null &&
          movement.paymentMethod !== PaymentMethod.Cash
            ? PAYMENT_METHODS_MAP.get(movement.paymentMethod)?.title
            : null;

        encoder.keyValueSpaceBetween(
          `${formatDateShort(movement.createdAt)} ${
            methodTitle ? `${description} (${methodTitle})` : description
          }`,
          `${movement.type === 'inflow' ? '+' : '-'}${formatPrice(
            movement.amount
          )}`
        );
      }
    }

    encoder.separator();
    encoder.boldLine('VENTAS POR CATEGORÍA');
    encoder.separator();

    for (const cat of cajaResume.ordersAggregated) {
      encoder.size('normal');
      encoder.keyValueSpaceBetween(cat.categoryName, formatPrice(cat.total));
      encoder.size('small');
      for (const item of cat.items) {
        encoder.keyValueSpaceBetween(
          `${item.quantity}x ${item.itemName}`,
          formatPrice(item.total)
        );
      }
    }

    encoder.separator();

    // ------------------------------
    //  Cancelled orders details
    // ------------------------------
    if (cajaResume.canceledOrdersDetails?.length) {
      encoder.boldLine('PEDIDOS CANCELADOS');
      encoder.separator();

      encoder.size('small');

      const orderTypeTitles = {
        [OrderType.Seat]: ORDER_TYPES_MAP.get(
          OrderType.Seat
        ).title.toLowerCase(),
        [OrderType.Away]: ORDER_TYPES_MAP.get(
          OrderType.Away
        ).title.toLowerCase(),
        [OrderType.Delivery]: ORDER_TYPES_MAP.get(
          OrderType.Delivery
        ).title.toLowerCase(),
      } as Record<string, string>;

      for (const ord of cajaResume.canceledOrdersDetails) {
        const typeTitle = ord.type ? orderTypeTitles[ord.type] ?? ord.type : '';

        // Line 1: ID, creation date, type, total
        encoder.keyValueSpaceBetween(
          `#${ord.id} - ${formatDateShort(ord.createdAt)}`,
          formatPrice(ord.total)
        );

        // Line 2: Cancel date and payment method
        encoder.line(`Cancelado el ${formatDateShort(ord.cancelledAt)}`);

        // Line 3: type, table label, customer (each part only when present)
        const customerName = (ord.customerName ?? '').trim();
        const customerAddress = (ord.customerAddress ?? '').trim();
        let customerInfo = 'Pedido';
        if (typeTitle) customerInfo += ` ${typeTitle}`;
        if (ord.tableName) customerInfo += ` ${ord.tableName}`;
        if (customerName) customerInfo += ` de ${customerName}`;
        if (customerAddress) customerInfo += `, ${customerAddress}`;
        if (customerInfo !== 'Pedido') {
          encoder.line(customerInfo);
        }

        // Line 4: Items inline
        if (ord.items && ord.items.length) {
          const itemsInline = ord.items
            .map((it) => `${it.quantity}x ${it.itemName}`)
            .join(', ');
          encoder.line(itemsInline);
        }

        encoder.newline();
      }

      encoder.size('normal');

      encoder.separator();
    }

    // ------------------------------
    //  End cancelled orders section
    // ------------------------------

    printCajaResumeCanceledItems(encoder, cajaResume, {
      formatPrice,
      formatDateShort,
    });

    encoder.newline();

    encoder.align('center');

    encoder.line('-----');

    encoder.align('left');

    // null feedLines = this document's historical feed (byte parity).
    encoder.newline(escpos.feedLines ?? 2);
    encoder.size('normal');

    encoder.cut(escpos.cutType, escpos.cutMode);

    const printData = encoder.encode();

    const printedBytes = await printByteArray(printer, printData, {
      kind: 'ticket',
      label: `Resumen de caja ${cajaResume.caja.name}`,
    });

    store.addPrintJob({
      printerType: PrinterType.Ticket,
      printerName: printer?.name,
      createdAt: new Date(),
      status: 'printed',
      data: printedBytes,
      title: `Resumen de caja ${cajaResume.caja.name}`,
    });
  }

  /**
   * ESC/POS "Cierre del día" in the redesigned owner-friendly order:
   * header → AVISOS → hero → TUS VENTAS → CÓMO TE PAGARON → CAJA Y
   * EFECTIVO → LO MÁS VENDIDO → CLUB → COBROS NO VENTA → AJUSTES.
   * Double-width/bold only for the title, Ventas netas del día (hero) and
   * Total del día; the caja card's "Cobrado del día" line is bold at normal
   * size; live previews are watermarked. New v2 payload fields (channels,
   * topProducts, otherDaysCollections, uncollectedSales) are null-guarded
   * so schema-1 snapshots print without them.
   */
  async function printDayClose(
    payload: IDayClose,
    options: { reprint?: boolean; live?: boolean } = {}
  ) {
    const restaurant = vueData.restaurant;
    const branch = vueData.branch;
    const header = payload.header;

    vueData.addAlert('Imprimiendo cierre del día...', AlertType.Info);

    const printer = store.ticketPrinter;
    const { escpos } = store.printerSettingsOf(
      printer,
      store.ticketPrintingSettings.encoding
    );

    const bucketLabels: Record<DayClosePaymentBucket, string> = {
      cash: 'Efectivo',
      card: 'Tarjeta',
      online: 'Online',
      banktransfer: 'Transferencia',
      qr: 'QR',
      invitation: 'Invitación',
      employee: 'Consumo empleado',
      customer: 'Saldo cliente',
      club: 'Saldo Club',
    };
    const discountTypeLabels: Record<string, string> = {
      manual: 'Manual',
      coupon: 'Cupón',
      rule: 'Promoción',
      club: 'Club',
    };

    function formatPrice(price: number) {
      if (price !== 0 && !price) return `${vueData.currency.symbol}-`;
      return `${vueData.currency.symbol}${displayPrice(price, true)}`;
    }

    /** Sign-aware variant: −$4.210 instead of $-4.210 (negative net /
     *  Cobrado edge cases). */
    function formatPriceSigned(price: number) {
      return price < 0
        ? `-${formatPrice(Math.abs(price))}`
        : formatPrice(price);
    }

    function formatDateTime(iso: string | null) {
      if (!iso) return '-';
      return new Date(iso).toLocaleString('es-AR', {
        timeZone: header.timeZone,
        hour12: false,
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    function formatBusinessDate(dateStr: string) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }

    // Shared display helpers: old-API fallbacks (net comps deduction,
    // collectedTotal Σ-rows backfill) consistent with the page hero.
    const net = getDayCloseNetDisplay(payload);
    const collected = getDayCloseCollectedDisplay(payload);

    const encoder = encoderForSettings(escpos);

    encoder.codepage(escpos.codepage);
    encoder.align('left');

    // ------------------------- Title -------------------------
    encoder.align('center');
    if (options.live) {
      encoder.boldLine('EN VIVO - NO ES CIERRE');
      encoder.newline();
    }
    encoder.setSize(2, 2);
    encoder.bold(true);
    encoder.line(
      options.live ? 'CIERRE DEL DÍA' : `CIERRE DEL DÍA Nº ${header.seq}`
    );
    encoder.bold(false);
    encoder.setSize(1, 1);
    if (header.version > 1) {
      encoder.boldLine(`CORREGIDO - versión ${header.version}`);
    }
    if (options.reprint) {
      encoder.boldLine('REIMPRESIÓN');
    }
    encoder.newline();
    encoder.align('left');
    encoder.size('small');

    // ------------------------- Header -------------------------
    encoder.keyValue('Tienda:', restaurant.name);
    encoder.keyValue('Sucursal:', branch.name);
    encoder.keyValue('Día comercial:', formatBusinessDate(header.businessDate));
    encoder.keyValue(
      'Ventana:',
      `${formatDateTime(header.windowFrom)} -> ${formatDateTime(
        header.windowTo
      )}`
    );
    encoder.keyValue('Generado:', formatDateTime(header.generatedAt));

    // ------------------------- Avisos -------------------------
    const staleSessions = payload.cashSessions.filter(
      (session) => session.state === 'open' && session.spansMultipleDays
    );
    const avisos: string[] = [];
    if (header.openOrdersCarried > 0) {
      avisos.push(
        `${header.openOrdersCarried} pedidos del día quedaron sin cerrar y no están incluidos en estos números.`
      );
    }
    if (staleSessions.length > 0) {
      avisos.push(
        `${staleSessions.length} cajas siguen abiertas de días anteriores. Ciérrelas para que los números cuadren.`
      );
    }
    if (header.mixedCurrencies) {
      avisos.push(
        `Hubo cobros en más de una moneda. Los totales se muestran en ${header.currency}.`
      );
    }
    if (header.inconsistencies > 0) {
      avisos.push(
        `Detectamos ${header.inconsistencies} pedidos con totales que no cierran. Revíselos en el panel.`
      );
    }
    if (avisos.length) {
      encoder.separator();
      encoder.boldLine('AVISOS');
      encoder.separator();
      for (const aviso of avisos) {
        encoder.line(`! ${aviso}`);
      }
    }

    // ------------------------- Hero -------------------------
    const wf = payload.waterfall;
    encoder.separator();
    encoder.boldLine('VENTAS NETAS DEL DÍA');
    encoder.separator();

    encoder.setSize(2, 2);
    encoder.bold(true);
    encoder.line(formatPriceSigned(net.netSales));
    encoder.bold(false);
    encoder.setSize(1, 1);
    encoder.size('small');
    encoder.keyValueSpaceBetween(
      'Cobrado:',
      formatPriceSigned(collected.collectedTotal)
    );
    encoder.keyValueSpaceBetween('Pedidos:', String(payload.kpis.orders));
    encoder.keyValueSpaceBetween(
      'Ticket promedio:',
      formatPrice(payload.kpis.avgTicket)
    );
    if (payload.kpis.covers != null) {
      // `undefined > 0` is false: old snapshots print the plain line.
      const sittingsWithoutCovers = payload.kpis.sittingsWithoutCovers;
      const coversValue =
        sittingsWithoutCovers != null && sittingsWithoutCovers > 0
          ? `${payload.kpis.covers} (${sittingsWithoutCovers} mesa${
              sittingsWithoutCovers === 1 ? '' : 's'
            } sin datos)`
          : String(payload.kpis.covers);
      encoder.keyValueSpaceBetween('Cubiertos:', coversValue);
    }
    if (payload.kpis.spendPerCover != null) {
      encoder.keyValueSpaceBetween(
        'Gasto por cubierto:',
        formatPrice(payload.kpis.spendPerCover)
      );
    }
    if (wf.tips !== 0) {
      encoder.keyValueSpaceBetween('Propinas:', formatPrice(wf.tips));
    }

    // ------------------------- Sus ventas -------------------------
    encoder.separator();
    encoder.boldLine('SUS VENTAS');
    encoder.separator();
    encoder.keyValueSpaceBetween('Vendió:', formatPrice(wf.grossSales));
    encoder.keyValueSpaceBetween(
      'Descuentos y promociones:',
      `-${formatPrice(wf.benefitsTotal)}`
    );
    for (const row of wf.benefitsByType) {
      encoder.keyValueSpaceBetween(
        `  ${discountTypeLabels[row.type] ?? row.type}:`,
        `-${formatPrice(row.amount)} (${row.count})`
      );
    }
    if (wf.clubBonusConsumed > 0) {
      encoder.keyValueSpaceBetween(
        '  (Bono Club utilizado:',
        `${formatPrice(wf.clubBonusConsumed)})`
      );
    }
    if (net.nonMonetary !== 0) {
      encoder.keyValueSpaceBetween(
        'Invitaciones y personal:',
        `-${formatPrice(net.nonMonetary)}`
      );
    }
    encoder.keyValueSpaceBetween(
      'Ventas netas:',
      formatPriceSigned(net.netSales)
    );
    // Re-add bridge: comps come back in so the printed chain to Total del
    // día stays provable (dayTotal re-includes them by identity).
    if (net.nonMonetary !== 0) {
      encoder.keyValueSpaceBetween(
        'Invitaciones y personal:',
        `+${formatPrice(net.nonMonetary)}`
      );
      encoder.line('(no se cobran, pero cuentan en el total)');
    }
    if (wf.taxes !== 0) {
      encoder.keyValueSpaceBetween('Impuestos:', `+${formatPrice(wf.taxes)}`);
    }
    if (wf.rounding !== 0) {
      encoder.keyValueSpaceBetween(
        'Redondeo:',
        `${wf.rounding > 0 ? '+' : '-'}${formatPrice(Math.abs(wf.rounding))}`
      );
    }
    encoder.setSize(2, 2);
    encoder.bold(true);
    encoder.line(`Total del día: ${formatPrice(wf.dayTotal)}`);
    encoder.bold(false);
    encoder.setSize(1, 1);
    encoder.size('small');
    if (wf.tips !== 0) {
      encoder.keyValueSpaceBetween('Propinas (aparte):', formatPrice(wf.tips));
    }
    const channelLabels: Record<string, string> = {
      seat: 'En mesa',
      away: 'Para llevar',
      delivery: 'Delivery',
    };
    const channels = payload.channels ?? [];
    if (channels.length) {
      encoder.newline();
      for (const channel of channels) {
        encoder.keyValueSpaceBetween(
          `${channelLabels[channel.type] ?? channel.type} (${channel.orders}):`,
          formatPrice(channel.amount)
        );
      }
    }

    // ------------------------- Cómo le pagaron -------------------------
    encoder.separator();
    encoder.boldLine('CÓMO LE PAGARON');
    encoder.separator();
    for (const row of payload.tenders.rows) {
      const label = bucketLabels[row.bucket] ?? row.bucket;
      encoder.keyValueSpaceBetween(
        `${label}${row.balancePayment ? ' (saldo)' : ''}:`,
        `${formatPrice(row.amount)} (${row.count})`
      );
    }
    encoder.newline();
    encoder.keyValueSpaceBetween(
      'Cobrado por ventas:',
      formatPrice(payload.tenders.collected)
    );
    const comps = payload.tenders.comps;
    if (comps.invitation.count > 0 || comps.employee.count > 0) {
      encoder.newline();
      encoder.line('Cortesías (sin cargo):');
      encoder.keyValueSpaceBetween(
        'Invitaciones:',
        `${formatPrice(comps.invitation.amount)} (${comps.invitation.count})`
      );
      encoder.keyValueSpaceBetween(
        'Consumo empleado:',
        `${formatPrice(comps.employee.amount)} (${comps.employee.count})`
      );
    }

    // ------------------------- Caja y efectivo -------------------------
    const col = payload.collections;
    const relevantSessions = payload.cashSessions.filter(
      (session) =>
        session.state === 'closed' ||
        (session.state === 'open' && !session.spansMultipleDays)
    );
    encoder.separator();
    encoder.boldLine('CAJA Y EFECTIVO');
    encoder.separator();
    if (col.source === 'estimated') {
      encoder.line('(Estimado: esta sucursal no usa cajas)');
    }
    for (const session of relevantSessions) {
      if (session.state === 'open') {
        encoder.keyValueSpaceBetween(
          `${session.cajaName}:`,
          `abierta desde ${formatDateTime(session.openedAt)}`
        );
      } else {
        encoder.keyValueSpaceBetween(
          `${session.cajaName}:`,
          `${formatDateTime(session.openedAt)} -> ${formatDateTime(
            session.closedAt
          )}`
        );
      }
      // Neutral fact only (mirrors the cierre-de-caja resume): never
      // esperado/Faltaron/Sobraron wording on owner-facing surfaces.
      if (session.cashCounted != null) {
        encoder.keyValueSpaceBetween(
          '  Efectivo al cerrar:',
          formatPrice(session.cashCounted)
        );
      }
    }
    if (staleSessions.length > 0) {
      encoder.keyValueSpaceBetween(
        'Cajas abiertas de días anteriores:',
        String(staleSessions.length)
      );
    }
    for (const row of col.rows) {
      const label = bucketLabels[row.bucket] ?? row.bucket;
      // Inflows can be negative: a payment-method change is netted as a
      // transfer, and its outflow leg may land on a later business date.
      encoder.keyValueSpaceBetween(
        `${label}:`,
        row.outflows
          ? `${formatPriceSigned(row.inflows)} / -${formatPrice(row.outflows)}`
          : formatPriceSigned(row.inflows)
      );
    }
    // Cobrado trail: Recibiste − Devoluciones = Cobrado del día (the hero's
    // "Cobrado" figure). Collapses to the bold line alone when there are no
    // refunds; the explanatory note always prints under the bold line.
    if (collected.refunds !== 0) {
      encoder.keyValueSpaceBetween(
        'Recibió:',
        formatPriceSigned(collected.received)
      );
      encoder.keyValueSpaceBetween(
        'Devoluciones:',
        `-${formatPrice(collected.refunds)}`
      );
    }
    encoder.bold(true);
    encoder.keyValueSpaceBetween(
      'Cobrado del día:',
      formatPriceSigned(collected.collectedTotal)
    );
    encoder.bold(false);
    encoder.line('(Ventas y cargas de saldo, menos devoluciones)');
    if (col.manualInflows !== 0 || col.manualOutflows !== 0) {
      encoder.keyValueSpaceBetween(
        'Ingresos manuales a caja:',
        formatPrice(col.manualInflows)
      );
      encoder.keyValueSpaceBetween(
        'Retiros de caja:',
        formatPrice(col.manualOutflows)
      );
      // Suppressed without manual movements: it would just duplicate the
      // bold Cobrado figure a few lines above.
      encoder.keyValueSpaceBetween(
        'Total movido:',
        formatPriceSigned(col.total)
      );
    }
    const otherDaysCollections = col.otherDaysCollections ?? 0;
    if (otherDaysCollections > 0) {
      encoder.line(
        `Hoy también se cobraron ${formatPrice(
          otherDaysCollections
        )} de pedidos de otros días.`
      );
    }
    const uncollectedSales = col.uncollectedSales ?? 0;
    if (uncollectedSales > roundPrice(wf.dayTotal * 0.01)) {
      encoder.line(
        `${formatPrice(
          uncollectedSales
        )} de las ventas de hoy no pasaron por ninguna caja.`
      );
    }

    // ------------------------- Lo más vendido -------------------------
    const topProducts = payload.topProducts ?? [];
    if (payload.categories.length || topProducts.length) {
      encoder.separator();
      encoder.boldLine('LO MÁS VENDIDO');
      encoder.separator();
      for (const row of payload.categories) {
        encoder.size('normal');
        encoder.keyValueSpaceBetween(
          row.name,
          `${formatPrice(row.net)} (${row.pctOfTotal}%)`
        );
        encoder.size('small');
        for (const child of row.children ?? []) {
          encoder.keyValueSpaceBetween(
            `> ${child.quantity}x ${child.name}`,
            formatPrice(child.net)
          );
        }
      }
      encoder.size('small');
      if (topProducts.length) {
        if (payload.categories.length) {
          encoder.newline();
        }
        encoder.line('Productos más vendidos:');
        for (const product of topProducts) {
          encoder.keyValueSpaceBetween(
            `${product.quantity}x ${product.name}`,
            formatPrice(product.amount)
          );
        }
      }
    }

    // ------------------------- Club -------------------------
    const club = payload.club;
    if (
      club.topupsPrincipal !== 0 ||
      club.balanceSpent !== 0 ||
      club.pointsDiscounts !== 0
    ) {
      encoder.separator();
      encoder.boldLine('CLUB');
      encoder.separator();
      encoder.keyValueSpaceBetween(
        'Cargas de saldo (dinero):',
        formatPrice(club.topupsPrincipal)
      );
      encoder.keyValueSpaceBetween(
        'Bono regalado:',
        formatPrice(club.topupsBonus)
      );
      encoder.keyValueSpaceBetween(
        'Saldo Club consumido:',
        formatPrice(club.balanceSpent)
      );
      encoder.keyValueSpaceBetween(
        '  del cual bono:',
        formatPrice(club.bonusSpent)
      );
      encoder.keyValueSpaceBetween(
        'Descuentos por puntos:',
        formatPrice(club.pointsDiscounts)
      );
    }

    // ------------------------- Cobros no venta -------------------------
    const ns = payload.nonSaleCollections;
    if (ns.total !== 0 || ns.clubTopupsBonus !== 0) {
      encoder.separator();
      encoder.boldLine('COBROS NO VENTA');
      encoder.separator();
      if (ns.clubTopupsPrincipal !== 0) {
        encoder.keyValueSpaceBetween(
          'Cargas de Saldo Club:',
          formatPrice(ns.clubTopupsPrincipal)
        );
        for (const row of ns.clubTopupsByBucket) {
          encoder.keyValueSpaceBetween(
            `  ${bucketLabels[row.bucket] ?? row.bucket}:`,
            `${formatPrice(row.amount)} (${row.count})`
          );
        }
      }
      if (ns.clubTopupsBonus !== 0) {
        encoder.keyValueSpaceBetween(
          'Bono Club regalado:',
          formatPrice(ns.clubTopupsBonus)
        );
      }
      if (ns.balanceLoads !== 0) {
        encoder.keyValueSpaceBetween(
          'Cargas de saldo cliente:',
          formatPrice(ns.balanceLoads)
        );
      }
      encoder.keyValueSpaceBetween('Total:', formatPrice(ns.total));
    }

    // ------------------------- Ajustes -------------------------
    const adj = payload.adjustments;
    if (
      adj.canceledOrders.count > 0 ||
      adj.canceledItems.count > 0 ||
      adj.fullyDiscountedOrders.count > 0 ||
      adj.reimbursedKept.count > 0
    ) {
      encoder.separator();
      encoder.boldLine('AJUSTES');
      encoder.separator();
      if (adj.canceledOrders.count > 0) {
        encoder.keyValueSpaceBetween(
          'Pedidos cancelados/rechazados:',
          `${adj.canceledOrders.count} (${formatPrice(
            adj.canceledOrders.amount
          )})`
        );
      }
      if (adj.canceledItems.count > 0) {
        encoder.keyValueSpaceBetween(
          'Items cancelados:',
          `${adj.canceledItems.count} (${formatPrice(
            adj.canceledItems.amount
          )})`
        );
      }
      if (adj.fullyDiscountedOrders.count > 0) {
        encoder.keyValueSpaceBetween(
          'Pedidos 100% descontados:',
          `${adj.fullyDiscountedOrders.count} (${formatPrice(
            adj.fullyDiscountedOrders.amount
          )})`
        );
      }
      if (adj.reimbursedKept.count > 0) {
        encoder.keyValueSpaceBetween(
          'Cobros retenidos (reembolsos):',
          `${adj.reimbursedKept.count} (${formatPrice(
            adj.reimbursedKept.amount
          )})`
        );
      }
    }

    if (options.live) {
      encoder.newline();
      encoder.align('center');
      encoder.boldLine('EN VIVO - NO ES CIERRE');
      encoder.align('left');
    }

    // null feedLines = this document's historical feed (byte parity).
    encoder.newline(escpos.feedLines ?? 2);
    encoder.size('normal');

    encoder.cut(escpos.cutType, escpos.cutMode);

    const printData = encoder.encode();

    const title = `Cierre del día ${formatBusinessDate(header.businessDate)}`;
    const printedBytes = await printByteArray(printer, printData, {
      kind: 'ticket',
      label: title,
    });

    store.addPrintJob({
      printerType: PrinterType.Ticket,
      printerName: printer?.name,
      createdAt: new Date(),
      status: 'printed',
      data: printedBytes,
      title,
    });
  }

  async function printInvoice(invoicingItem: IInvoicingItem) {
    if (invoicingItem.invoicingData.platform !== 'ar-afip') {
      throw new Error('Plataforma de facturación no soportada');
    }

    // AFIP QR payload: explicit RG 4892 allowlist. Never spread the whole
    // data record (it now also carries caeVto/impNeto/impTotConc).
    const qrUrl = buildAfipQrUrl(invoicingItem.data);

    const ticketSettings = vueData.ticketSettings;
    const restaurant = vueData.restaurant;

    const printer = store.ticketPrinter;
    const { escpos } = store.printerSettingsOf(
      printer,
      store.ticketPrintingSettings.encoding
    );

    const encoder = encoderForSettings(escpos);

    encoder.codepage(escpos.codepage);
    encoder.align('left');

    encoder.size('normal');
    encoder.align('center');

    let title = '';

    switch (invoicingItem.data.tipoCmp) {
      case 1:
        title = 'FACTURA A';
        break;
      case 6:
        if (!invoicingItem.data.cuitCliente) title = 'CONSUMIDOR FINAL';
        else title = 'FACTURA B';
        break;
      case 11:
        if (!invoicingItem.data.cuitCliente) title = 'CONSUMIDOR FINAL';
        else title = 'FACTURA C';
        break;
    }

    encoder.boldLine(title);
    encoder.size('small');
    encoder.newline();
    encoder.align('left');

    encoder.keyValue('Tienda:', restaurant.name);

    // Emitter block (RG 1415): every line conditional — never print blank labels.
    const emitter = buildArAfipEmitter(
      invoicingItem.invoicingData.data ?? {},
      restaurantStore.selectedBranch?.address ?? null
    );
    const keyValueIfPresent = (label: string, value: string | null) => {
      if (typeof value === 'string' && value.trim() !== '') {
        encoder.keyValue(label, value.trim());
      }
    };
    keyValueIfPresent('Razón social:', emitter.razonSocial);
    keyValueIfPresent('Domicilio:', emitter.domicilio);
    keyValueIfPresent('Cond. IVA:', emitter.condicionIva);
    // Always the per-invoice payload CUIT (never the emitter profile's).
    const cuitDigits = String(invoicingItem.data.cuit ?? '').replace(/\D/g, '');
    if (cuitDigits.length === 11) {
      encoder.keyValue('CUIT:', cuitDigits);
    }
    keyValueIfPresent('IIBB:', emitter.ingresosBrutos);
    keyValueIfPresent('Inicio act.:', emitter.inicioActividades);

    encoder.separator();

    // Fecha = fiscal emission date (yyyy-mm-dd -> dd/mm/yyyy); the print
    // timestamp goes on its own line (parity with the POS formatter).
    const fiscalDate =
      typeof invoicingItem.data.fecha === 'string' && invoicingItem.data.fecha
        ? invoicingItem.data.fecha.split('-').reverse().join('/')
        : '';
    if (fiscalDate) encoder.keyValue('Fecha:', fiscalDate);
    encoder.keyValue('Fecha impresión:', new Date().toLocaleString());
    encoder.keyValue('Número:', invoicingItem.data.nroCmp);
    encoder.keyValue('Pto. de venta:', invoicingItem.data.ptoVta);

    // Receptor: only for identified buyers (99 / 0 mean consumidor final).
    const tipoDocRec = Number(invoicingItem.data.tipoDocRec ?? NaN);
    const nroDocRec = Number(invoicingItem.data.nroDocRec ?? 0);
    if (
      invoicingItem.data.tipoDocRec != null &&
      tipoDocRec !== 99 &&
      nroDocRec > 0
    ) {
      const receptorLabel =
        tipoDocRec === 80
          ? 'CUIT'
          : tipoDocRec === 86
          ? 'CUIL'
          : tipoDocRec === 96
          ? 'DNI'
          : 'Doc.';
      encoder.keyValue(`${receptorLabel}:`, String(nroDocRec));

      // RG 5616 receptor data (only present on newer invoices).
      const razonSocialRec =
        typeof invoicingItem.data.razonSocialRec === 'string'
          ? invoicingItem.data.razonSocialRec.trim()
          : '';
      if (razonSocialRec) {
        encoder.keyValue('Razón social:', razonSocialRec);
      }

      const condIvaRecLabels: Record<number, string> = {
        1: 'Responsable Inscripto',
        4: 'IVA Sujeto Exento',
        5: 'Consumidor Final',
        6: 'Responsable Monotributo',
        7: 'Sujeto No Categorizado',
      };
      const condIvaRecLabel =
        condIvaRecLabels[Number(invoicingItem.data.condIvaRec ?? NaN)];
      if (condIvaRecLabel) {
        encoder.keyValue('Cond. IVA:', condIvaRecLabel);
      }
    }

    /* encoder.separator();
    encoder.boldLine('DETALLE');
    encoder.separator();

    for (const item of invoicingItem.items) {
      encoder.keyValueSpaceBetween(item.description, item.total);
    } */

    encoder.separator();
    encoder.boldLine('TOTALES');
    encoder.separator();

    encoder.keyValueSpaceBetween(
      'Total',
      vueData.currency.symbol + roundPrice(invoicingItem.total)
    );

    // Add transparency fiscal section for Argentina
    if (restaurant.country === 'AR') {
      // Calculate VAT based on invoice data - AFIP provides this info in the invoice data
      let vatAmount = 0;
      let otherTaxesAmount = 0;

      if (invoicingItem.data.impIVA) {
        vatAmount = parseFloat(invoicingItem.data.impIVA);
      }

      if (invoicingItem.data.impTrib) {
        otherTaxesAmount = parseFloat(invoicingItem.data.impTrib);
      }

      // Add the transparency fiscal section
      if (vatAmount > 0) {
        _printTransparencyFiscal(encoder, {
          vatOverride: vatAmount,
          otherTaxesOverride: otherTaxesAmount,
          currency: vueData.currency,
        });
      }
    }

    encoder.newline();

    encoder.align('center');

    await renderQr(encoder, qrUrl, {
      qrMode: escpos.qrMode,
      paperWidthMm: escpos.paperWidthMm,
    });

    encoder.newline();
    encoder.keyValue('CAE:', invoicingItem.data.codAut);
    // caeVto (yyyy-mm-dd -> dd/mm/yyyy); pre-deploy invoices lack it — omit.
    if (
      typeof invoicingItem.data.caeVto === 'string' &&
      invoicingItem.data.caeVto
    ) {
      const caeVto = invoicingItem.data.caeVto.split('-').reverse().join('/');
      encoder.keyValue('Vencimiento CAE:', caeVto);
    }

    encoder.newline();
    encoder.align('center');
    encoder.line('-----');
    encoder.align('left');
    // null feedLines = this document's historical feed (byte parity).
    encoder.newline(escpos.feedLines ?? 2);

    encoder.cut(escpos.cutType, escpos.cutMode);

    const printData = encoder.encode();

    const printedBytes = await printByteArray(printer, printData, {
      kind: 'ticket',
      label: title,
    });

    store.addPrintJob({
      printerType: PrinterType.Ticket,
      printerName: printer?.name,
      createdAt: new Date(),
      status: 'printed',
      data: printedBytes,
      title,
    });
  }

  //#endregion

  function isPrinterConnected(printerType: PrinterType) {
    if (strategy.value.status.value !== PrintPluginStatus.Connected)
      return false;

    switch (printerType) {
      case PrinterType.Ticket:
        return !!store.ticketPrinter;
      case PrinterType.Comanda:
        return _hasAnyComandaPrinter();
      case PrinterType.Etiqueta:
        return !!store.stickerPrinter;
    }
  }

  return markRaw({
    printers,
    setPrinters: store.setPrinters,
    stickerPrinter,
    setStickerPrinter: store.setStickerPrinter,
    comandaPrintersByStation,
    setComandaPrinter: store.setComandaPrinter,
    ticketPrinter,
    setTicketPrinter: store.setTicketPrinter,
    stickerPrintingSettings,
    comandaPrintingSettings,
    ticketPrintingSettings,
    getPrinters,
    reloadAllPrinters,
    // Strategy-agnostic delegates: consumers mounted before a strategy swap
    // (bridge adoption) must not stay bound to the old strategy.
    status: computed(() => store.getStrategy().status.value),
    connect: () => store.getStrategy().connect(),
    autoPrintRetryableFailureEpoch: computed(
      () => store.autoPrintRetryableFailureEpoch
    ),
    hasInFlightAutoPrintEvaluations,
    clearPendingGroupPrints,
    pluginVersion,
    isOutdatedVersion,
    upgradeAvailable,
    isPrinterConnected,
    printRawBytes: (data: IPrintRawBytesParams) =>
      store.getStrategy().printRawBytes(data),
    printZpl,
    printByteArray,
    onOrderChange,
    evaluateOrderAutoPrint,
    printOrder,
    printOrderSummary,
    printGroup,
    getManualPrintGate,
    printCajaResume,
    printReservationsDayList,
    printDayClose,
    printInvoice,
  });
}
